// @spec GHG-STR-010
// @spec GHG-STR-102
'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Copy, Loader2 } from 'lucide-react';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as z from 'zod';

import {
  useConfirmActivityAssignment,
  useSuggestActivityAssignment,
} from '../api/use-activity-assignment';
import {
  useActivityAutopilotEstimate,
  useSuggestActivityAutopilot,
} from '../api/use-activity-autopilot';
import {
  useActivityCatalogEntries,
  useActivityUnitTypeCounts,
  useBootstrapActivityCatalog,
  usePromoteActivityCatalogEntry,
} from '../api/use-activity-catalog';
import {
  useActivityEstimatePreview,
  useCreateActivityData,
  useUpdateActivityData,
} from '../api/use-activity-data';
import { useEmissionFactor } from '../api/use-emission-factors';
import { useFacilities } from '../api/use-facilities';
import { buildFactorSelectionPayload, useResolveFactor } from '../api/use-factor-resolution';
import { useGridRegions } from '../api/use-grid-regions';
import { useResolveScope3WasteCode } from '../api/use-scope3-waste-codes';
import {
  getAllowedActivityMethodsForCalculationMethod,
  getCalculationMethodOptions,
  inferCalculationMethodForEntry,
  isCalculationMethodCompatible,
} from '../config/activity-calculation-methods';
import {
  defaultScope2EnergyTypeForCategory,
  defaultScope3CategoryForSelection,
  getActivityEntryScopeOptions,
  getCategoryOptionsForScope,
  resolveSelectionToActivityCategory,
  type ActivityScopeOptionValue,
} from '../config/activity-entry-catalog';
import {
  getDefaultMethodForCategory,
  getDefaultUnitForCategory,
} from '../config/category-method-defaults';
import {
  getActivitiesFor,
  getPresetUnitType,
  inferUnitTypeFromDefaultUnit,
  toActivityTypeValue,
} from '../config/climatiq-activity-catalog';
import type { EmissionFactorSearchContext } from '../hooks/useEmissionFactorSearchState';
import { useMappingMemorySuggestions } from '../api/use-mapping-memory';
import { ActivityDescriptionAutocomplete } from './ActivityDescriptionAutocomplete';
import { FactorSegmentedSelector, type FactorSelectorSegment } from './FactorSegmentedSelector';
import type { FactorSourceChoice } from './FactorSourceSelector';
import {
  buildAutopilotActivityValue,
  buildAutopilotEstimateParameters,
  isScope3AutopilotCategory,
  mapAutopilotEstimateToResolvedCandidate,
} from '../lib/activity-autopilot';
import { resolveClimatiqSourceForActivity } from '../lib/climatiq-source-policy';
import {
  getParameterSpec,
  UNIT_KEY_LABELS,
  VALID_UNITS,
  VALUE_KEY_LABELS,
} from '../lib/unit-conversion';
import type {
  ActivityDiscoveryCandidate,
  EmissionFactor,
  EmissionFactorSearchParams,
  GHGScope,
  ResolveFactorCandidate,
} from '../types';
import type { AlternativeCandidate, AssignmentCandidate } from '../types/activity-assignment';
import type {
  ActivityAutopilotEmissionFactorSummary,
  ActivityAutopilotSuggestionItem,
} from '../types/activity-autopilot';
import type { ActivityDataCalculationMethod } from '../types/activity-calculation-method';
import type {
  ActivityDataResponse,
  CreateActivityDataInput,
  EmissionFactorSelectorSource,
  FactorSelectionMode,
  FactorSelectionInput,
  Scope2AccountingMethod,
  Scope2EnergyType,
} from '../types/activity-data';
import { SCOPE3_CATEGORIES_META } from '../types/ghg-settings';
import type {
  Scope3WasteCodeMatch,
  Scope3WasteCodeResolutionResponse,
  Scope3WasteCodeTreatmentOption,
} from '../types/scope3-waste-code';

import { ActivityAssignmentCandidateCard } from './ActivityAssignmentCandidateCard';
import { ActivityFormEstimateInline } from './ActivityFormEstimateInline';
import { ActivityFormFactorBadge } from './ActivityFormFactorBadge';
import { ActivityFormHeader } from './ActivityFormHeader';
import { ActivityFormQuantityInputs } from './ActivityFormQuantityInputs';
import { ActivityFormStepOne } from './ActivityFormStepOne';
import { EmissionFactorSearch } from './EmissionFactorSearch';
import { Scope3ActivityPicker } from './Scope3ActivityPicker';
import { buildSelectableReportingPeriods } from './activity-reporting-period-options';
import { FacilityField, ReportingPeriodField } from './data-entry-form-shared';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { Scope3CategoryQuickTemplate } from '@/features/ghg/config/scope3-categories';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import { ApiClientError } from '@/types/api';

const NO_SELECTION = '__none__';
const DISCOVERED_ACTIVITY_VALUE = '__discovered_activity__';
const DEFAULT_BOOTSTRAP_PER_METHOD_LIMIT = 500;
const ATTEMPTED_BOOTSTRAP_CONTEXTS = new Set<string>();
const IN_FLIGHT_BOOTSTRAP_CONTEXTS = new Set<string>();
const BOOTSTRAPPED_CONTEXTS = new Set<string>();

const SUPPORTED_METHODS = [
  'Area',
  'AreaOverTime',
  'ContainerOverDistance',
  'Data',
  'DataOverTime',
  'Distance',
  'DistanceOverTime',
  'Energy',
  'Money',
  'Number',
  'NumberOverTime',
  'PassengerOverDistance',
  'Power',
  'Time',
  'Volume',
  'Weight',
  'WeightOverDistance',
  'WeightOverTime',
] as const;
const SCOPE2_ALLOWED_METHODS = ['Energy', 'Money'] as const;
const SCOPE2_ALLOWED_METHOD_SET = new Set<string>(SCOPE2_ALLOWED_METHODS);

function isMethodAllowedForScope(scope: GHGScope, method: string): boolean {
  if (scope !== 2) return true;
  return SCOPE2_ALLOWED_METHOD_SET.has(method);
}

const FALLBACK_SCOPE2_COUNTRY_CODES = [
  'US',
  'GB',
  'DE',
  'FR',
  'IT',
  'ES',
  'NL',
  'PL',
  'SE',
  'NO',
  'DK',
  'FI',
  'CA',
  'AU',
  'JP',
  'IN',
  'BR',
  'MX',
  'AE',
  'ZA',
] as const;

const COUNTRY_NAME_RESOLVER =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames !== 'undefined'
    ? new Intl.DisplayNames(
        typeof navigator !== 'undefined' && navigator.languages.length > 0
          ? navigator.languages
          : undefined,
        { type: 'region' }
      )
    : null;

function formatMethodLabel(method: string): string {
  return method.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function factorSelectionModeLabel(mode: FactorSelectionMode | undefined, t: (key: string) => string): string {
  if (mode === 'manual') return t('activityDataEntryForm.label_manually_selected');
  if (mode === 'auto') return t('activityDataEntryForm.label_auto_selected');
  return t('activityDataEntryForm.label_pending_selection');
}

function factorSelectionModeClasses(mode: FactorSelectionMode | undefined): string {
  if (mode === 'manual') return 'bg-amber-100 text-amber-900';
  if (mode === 'auto') return 'bg-emerald-100 text-emerald-900';
  return 'bg-muted text-muted-foreground';
}

function shortenFactorId(id: string): string {
  if (id.length <= 20) return id;
  return `${id.slice(0, 8)}...${id.slice(-6)}`;
}

function formatCountryOption(code: string): string {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return '';
  const displayName = COUNTRY_NAME_RESOLVER?.of(normalized);
  return displayName ? `${displayName} (${normalized})` : normalized;
}

function normalizeScope2EnergyType(value: string): Scope2EnergyType | '' {
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'electricity' ||
    normalized === 'steam' ||
    normalized === 'heating' ||
    normalized === 'cooling'
  ) {
    return normalized;
  }
  return '';
}

function getImplicitScope2ActivityType(energyType: Scope2EnergyType | '', t?: (key: string) => string): string {
  if (energyType === 'electricity') return t ? t('activityDataEntryForm.label_purchased_electricity') : 'Purchased electricity';
  return '';
}

function formatKgCo2e(value: number | null | undefined, t: (key: string) => string): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return t('activityDataEntryForm.label_not_available');
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 6 })} kg CO2e`;
}

function formatScope2MethodLabel(value: string | null | undefined, t: (key: string) => string): string {
  if (value === 'location_based') return t('activityDataEntryForm.label_location_based');
  if (value === 'market_based') return t('activityDataEntryForm.label_market_based');
  if (value === 'both') return t('activityDataEntryForm.label_both_methods');
  return t('activityDataEntryForm.label_not_available');
}

function buildAssignmentSignature(input: {
  scope: GHGScope;
  activityCategory: string;
  activityMethod: string;
  activityDescription: string;
}): string {
  return [
    String(input.scope),
    input.activityCategory.trim().toLowerCase(),
    input.activityMethod.trim(),
    input.activityDescription.trim().toLowerCase(),
  ].join('|');
}

function deriveSummaryForMethod(input: {
  activityMethod: string;
  methodValues: Record<string, number>;
  methodUnits: Record<string, string>;
}): { quantity: number; unit: string; display: string } {
  const mapping: Record<string, { valueKey: string; unitKey: string; fixedUnit?: string }> = {
    Money: { valueKey: 'money', unitKey: 'money_unit' },
    Distance: { valueKey: 'distance', unitKey: 'distance_unit' },
    Energy: { valueKey: 'energy', unitKey: 'energy_unit' },
    Volume: { valueKey: 'volume', unitKey: 'volume_unit' },
    Weight: { valueKey: 'weight', unitKey: 'weight_unit' },
    PassengerOverDistance: {
      valueKey: 'passengers',
      unitKey: 'distance_unit',
      fixedUnit: 'passenger',
    },
    WeightOverDistance: { valueKey: 'weight', unitKey: 'weight_unit' },
    ContainerOverDistance: { valueKey: 'containers', unitKey: '', fixedUnit: 'container' },
    AreaOverTime: { valueKey: 'area', unitKey: 'area_unit' },
    DataOverTime: { valueKey: 'data', unitKey: 'data_unit' },
    DistanceOverTime: { valueKey: 'distance', unitKey: 'distance_unit' },
    NumberOverTime: { valueKey: 'number', unitKey: '' },
    WeightOverTime: { valueKey: 'weight', unitKey: 'weight_unit' },
  };

  const preferred = mapping[input.activityMethod];
  let valueKey = preferred?.valueKey ?? '';
  if (!valueKey) {
    valueKey = Object.keys(input.methodValues)[0] ?? '';
  }
  if (!valueKey) {
    return { quantity: 0, unit: '', display: '' };
  }

  const quantity = input.methodValues[valueKey] ?? 0;
  const resolvedUnit =
    preferred?.fixedUnit ??
    (preferred?.unitKey
      ? (input.methodUnits[preferred.unitKey] ?? '')
      : (input.methodUnits[`${valueKey}_unit`] ?? ''));

  return {
    quantity,
    unit: resolvedUnit,
    display: `${String(quantity)} ${resolvedUnit}`.trim(),
  };
}

function toMethodKeyedStrings(values: Record<string, number> | undefined): Record<string, string> {
  if (!values) return {};
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, String(value)]));
}

function getMatchedWasteCodeEntry(
  response: Scope3WasteCodeResolutionResponse | undefined
): Scope3WasteCodeMatch | null {
  if (response?.status !== 'matched' || !response.matchedEntry) {
    return null;
  }
  return response.matchedEntry;
}

function getMatchedWasteCodeTreatment(
  entry: Scope3WasteCodeMatch | null,
  treatmentId: string
): Scope3WasteCodeTreatmentOption | null {
  if (!entry || !treatmentId.trim()) return null;
  return entry.treatmentOptions.find((option) => option.treatmentId === treatmentId) ?? null;
}

function buildWasteCodeResolutionMessage(
  response: Scope3WasteCodeResolutionResponse | undefined
): string | null {
  if (!response) return null;
  if (response.status === 'matched' && response.matchedEntry) {
    return `${response.matchedEntry.canonicalCode} matched to ${response.matchedEntry.familyLabel} (${response.matchedEntry.source}).`;
  }
  return response.message ?? null;
}

function asStringRecordValue(
  record: Record<string, unknown> | null | undefined,
  key: string
): string {
  const value = record?.[key];
  return typeof value === 'string' ? value : '';
}

type ActivityChoiceSource =
  | 'curated'
  | 'org_catalog'
  | 'discovered'
  | 'existing'
  | 'manual'
  | 'autopilot'
  | 'ewc_mapping'
  | 'mapping_memory'
  | 'predefined';
type Scope2MarketInputMode = 'same_as_location' | 'market_factor' | 'manual_total';

interface ActivityChoice {
  value: string;
  label: string;
  activityId: string;
  activitySource: 'climatiq' | 'custom';
  unitType: string;
  defaultUnit?: string;
  allowedUnits?: string[];
  notes?: string;
  source: ActivityChoiceSource;
  presetId?: string;
}

const SCOPE3_CATEGORY_OPTIONS = SCOPE3_CATEGORIES_META.map((category) => ({
  value: String(category.number),
  label: `${String(category.number)} - ${category.name}`,
}));
const DATA_ACCURACY_OPTIONS = Array.from({ length: 10 }, (_value, index) => ({
  value: String(index + 1),
  label: `${String(index + 1)} / 10`,
}));

const formSchema = z
  .object({
    reportingPeriodId: z.string().min(1, 'Reporting period is required'),
    scope: z.union([z.literal('1'), z.literal('2'), z.literal('3')]),
    activityCategory: z.string().trim().min(1, 'Activity category is required').max(100),
    calculationMethod: z.string().trim(),
    activityMethod: z.string().trim().min(1, 'Unit type is required').max(50),
    activityType: z.string().trim().max(300),
    activityDescription: z.string().trim().max(500),
    facilityId: z.string(),
    scope3Category: z.string(),
    scope2EnergyType: z.string(),
    scope2AccountingMethod: z.string(),
    scope2Country: z.string(),
    scope2RegionCode: z.string(),
    scope2MarketInputMode: z.string(),
    scope2Supplier: z.string().max(120),
    scope2MarketEmissionsKg: z.string(),
    scope2LegacyMarketFactorKgCo2ePerKwh: z.string(),
    emissionFactorId: z.string(),
    dataAccuracyScore: z.string(),
    dataAssumptions: z.string().max(2000),
    dataIsEstimated: z.boolean(),
    notes: z.string().max(2000),
  })
  .superRefine((values, context) => {
    const scope = Number(values.scope);
    if (scope !== 2 && !values.calculationMethod.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Calculation method is required.',
        path: ['calculationMethod'],
      });
    }
    const scope2EnergyType =
      values.scope2EnergyType.trim() || defaultScope2EnergyTypeForCategory(values.activityCategory);
    const implicitScope2ActivityType =
      scope === 2 ? getImplicitScope2ActivityType(normalizeScope2EnergyType(scope2EnergyType)) : '';

    // Facility is optional for all scopes — users may not have facilities
    // configured yet during initial data entry.
    if (!values.activityType.trim() && !implicitScope2ActivityType) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Activity is required.',
        path: ['activityType'],
      });
    }
    if (scope === 2 && !scope2EnergyType) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scope 2 requires energy type.',
        path: ['scope2EnergyType'],
      });
    }
    if (scope === 2 && !values.scope2Country.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scope 2 requires country.',
        path: ['scope2Country'],
      });
    }
    if (scope === 2 && values.scope2Country.trim() && !/^[A-Z]{2}$/.test(values.scope2Country)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scope 2 country must be ISO-3166 alpha-2 code.',
        path: ['scope2Country'],
      });
    }
    const scope2MarketInputMode = values.scope2MarketInputMode as Scope2MarketInputMode;
    const usesMarketFactorMode = scope === 2 && scope2MarketInputMode === 'market_factor';
    if (usesMarketFactorMode && values.scope2Supplier.trim()) {
      if (!/^[a-zA-Z0-9_\-\s]+$/.test(values.scope2Supplier)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Supplier accepts letters, numbers, spaces, hyphens, and underscores only.',
          path: ['scope2Supplier'],
        });
      }
      if (scope2EnergyType !== 'electricity') {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Supplier is supported only for Scope 2 electricity.',
          path: ['scope2Supplier'],
        });
      }
    }
    const marketEmissionsRaw =
      scope === 2 && scope2MarketInputMode === 'manual_total'
        ? values.scope2MarketEmissionsKg.trim()
        : '';
    if (scope === 2 && scope2MarketInputMode === 'manual_total' && marketEmissionsRaw) {
      const parsed = Number(marketEmissionsRaw);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Market emissions must be greater than 0.',
          path: ['scope2MarketEmissionsKg'],
        });
      }
    }
    if (
      scope === 2 &&
      values.activityMethod.trim() &&
      !SCOPE2_ALLOWED_METHODS.includes(values.activityMethod as 'Energy' | 'Money')
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scope 2 supports only Energy or Money unit types.',
        path: ['activityMethod'],
      });
    }

    if (scope === 3 && !values.scope3Category.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Scope 3 requires category number.',
        path: ['scope3Category'],
      });
    }

    if ((scope === 1 || scope === 3) && !values.emissionFactorId.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Emission factor is required for Scope 1 and Scope 3 entries.',
        path: ['emissionFactorId'],
      });
    }

    const accuracyScoreRaw = values.dataAccuracyScore.trim();
    if (accuracyScoreRaw) {
      const parsedAccuracyScore = Number(accuracyScoreRaw);
      if (
        !Number.isInteger(parsedAccuracyScore) ||
        parsedAccuracyScore < 1 ||
        parsedAccuracyScore > 10
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Data accuracy must be an integer from 1 to 10.',
          path: ['dataAccuracyScore'],
        });
      }
    }
  });

type FormValues = z.infer<typeof formSchema>;
type CreateStep = 1 | 2;

interface ActivityDataEntryFormProps {
  mode: 'create' | 'edit';
  existingEntry?: ActivityDataResponse | undefined;
  defaultReportingPeriodId?: string | undefined;
  fixedScope?: GHGScope | undefined;
  fixedScope3Category?: number | undefined;
  defaultCreateActivityMethod?: string | undefined;
  scope3TemplatePreset?: Scope3CategoryQuickTemplate | undefined;
  readOnly?: boolean | undefined;
  taskId?: string | undefined;
  factorSourceChoice?: FactorSourceChoice | undefined;
  onSuccess: (entry: ActivityDataResponse) => void;
  onCancel: () => void;
}

function toResolvedCandidateFromManualFactor(
  factor:
    | {
        id: string;
        activityId: string;
        name: string;
        source: string;
        region: string;
        regionName: string;
        year: number;
        unit: string;
        unitType: string;
        co2ePerUnit: number;
        selectorSource?: 'climatiq' | 'custom' | undefined;
      }
    | undefined,
  dataVersion: string
): ResolveFactorCandidate | undefined {
  if (!factor) return undefined;
  return {
    dataVersion,
    id: factor.id,
    activityId: factor.activityId,
    name: factor.name,
    source: factor.source,
    region: factor.region,
    regionName: factor.regionName,
    year: factor.year,
    unit: factor.unit,
    unitType: factor.unitType,
    co2ePerUnit: factor.co2ePerUnit,
    ...(factor.selectorSource ? { selectorSource: factor.selectorSource } : {}),
  };
}

export function ActivityDataEntryForm({
  mode,
  existingEntry,
  defaultReportingPeriodId,
  fixedScope,
  fixedScope3Category,
  defaultCreateActivityMethod,
  scope3TemplatePreset,
  readOnly = false,
  taskId,
  factorSourceChoice = 'mixed',
  onSuccess,
  onCancel,
}: ActivityDataEntryFormProps) {
  const { t } = useTranslation('ghg');
  const effectiveFixedScope = mode === 'create' ? fixedScope : undefined;
  const effectiveFixedScope3Category =
    mode === 'create' && effectiveFixedScope === 3 && Number.isInteger(fixedScope3Category)
      ? fixedScope3Category
      : undefined;
  const fixedScope3CategorySelection =
    effectiveFixedScope3Category != null &&
    effectiveFixedScope3Category >= 1 &&
    effectiveFixedScope3Category <= 15
      ? `scope3_category_${String(effectiveFixedScope3Category)}`
      : '';
  const { periodId, periods, isLoading: periodsLoading, setPeriodId } = useGHGPagePeriod();
  const selectablePeriods = useMemo(() => buildSelectableReportingPeriods(periods), [periods]);
  const { facilities, isLoading: facilitiesLoading } = useFacilities();

  const createMutation = useCreateActivityData();
  const updateMutation = useUpdateActivityData();
  const suggestAssignmentMutation = useSuggestActivityAssignment();
  const suggestAutopilotMutation = useSuggestActivityAutopilot();
  const confirmAssignmentMutation = useConfirmActivityAssignment();
  const bootstrapMutation = useBootstrapActivityCatalog();
  const promoteActivityMutation = usePromoteActivityCatalogEntry();

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const isEntryApproved = existingEntry?.status === 'approved';
  const isApproved = isEntryApproved || readOnly;

  const form = useForm<FormValues, undefined, FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reportingPeriodId: existingEntry?.reportingPeriodId ?? defaultReportingPeriodId ?? '',
      scope: String(existingEntry?.value.scope ?? effectiveFixedScope ?? 1) as '1' | '2' | '3',
      activityCategory: existingEntry?.value.activityCategory ?? '',
      calculationMethod: existingEntry?.value.calculationMethod ?? '',
      activityMethod: existingEntry?.value.activityMethod ?? '',
      activityType: existingEntry?.value.activityType ?? '',
      activityDescription: existingEntry?.value.activityDescription ?? '',
      facilityId: existingEntry?.facilityId ?? NO_SELECTION,
      scope3Category: existingEntry?.value.scope3Category
        ? String(existingEntry.value.scope3Category)
        : '',
      scope2EnergyType:
        typeof existingEntry?.value.scope2Context?.energyType === 'string'
          ? existingEntry.value.scope2Context.energyType
          : '',
      scope2AccountingMethod:
        typeof existingEntry?.value.scope2Context?.accountingMethod === 'string'
          ? existingEntry.value.scope2Context.accountingMethod
          : 'both',
      scope2Country:
        typeof existingEntry?.value.scope2Context?.country === 'string'
          ? existingEntry.value.scope2Context.country.toUpperCase()
          : '',
      scope2RegionCode:
        typeof existingEntry?.value.scope2Context?.regionCode === 'string'
          ? existingEntry.value.scope2Context.regionCode
          : NO_SELECTION,
      scope2MarketInputMode:
        typeof existingEntry?.value.scope2MarketEmissionsKg === 'number' ||
        typeof existingEntry?.value.scope2Context?.marketEmissionsKg === 'number'
          ? 'manual_total'
          : existingEntry?.value.scope2MarketFactor ||
              typeof existingEntry?.value.scope2Context?.marketFactorKgCo2ePerKwh === 'number'
            ? 'market_factor'
            : 'same_as_location',
      scope2Supplier:
        typeof existingEntry?.value.scope2Context?.supplier === 'string'
          ? existingEntry.value.scope2Context.supplier
          : '',
      scope2MarketEmissionsKg:
        typeof existingEntry?.value.scope2MarketEmissionsKg === 'number'
          ? String(existingEntry.value.scope2MarketEmissionsKg)
          : typeof existingEntry?.value.scope2Context?.marketEmissionsKg === 'number'
            ? String(existingEntry.value.scope2Context.marketEmissionsKg)
            : '',
      scope2LegacyMarketFactorKgCo2ePerKwh:
        typeof existingEntry?.value.scope2Context?.marketFactorKgCo2ePerKwh === 'number'
          ? String(existingEntry.value.scope2Context.marketFactorKgCo2ePerKwh)
          : '',
      emissionFactorId: existingEntry?.value.emissionFactorId ?? '',
      dataAccuracyScore:
        typeof existingEntry?.value.dataAccuracyScore === 'number'
          ? String(existingEntry.value.dataAccuracyScore)
          : '',
      dataAssumptions: existingEntry?.value.dataAssumptions ?? '',
      dataIsEstimated: existingEntry?.value.dataIsEstimated ?? false,
      notes: existingEntry?.notes ?? '',
    },
  });

  const [methodValues, setMethodValues] = useState<Record<string, string>>(
    toMethodKeyedStrings(existingEntry?.value.methodValues)
  );
  const [methodUnits, setMethodUnits] = useState<Record<string, string>>(
    existingEntry?.value.methodUnits ?? {}
  );

  const [createStep, setCreateStep] = useState<CreateStep>(mode === 'create' ? 1 : 2);
  const [createScopeSelection, setCreateScopeSelection] = useState<ActivityScopeOptionValue>(
    String(existingEntry?.value.scope ?? effectiveFixedScope ?? 1) as ActivityScopeOptionValue
  );
  const [createCategorySelection, setCreateCategorySelection] = useState(
    fixedScope3CategorySelection
  );
  const [createStepError, setCreateStepError] = useState('');
  const [methodFormError, setMethodFormError] = useState('');
  const [discoveredActivity, setDiscoveredActivity] = useState<ActivityDiscoveryCandidate | null>(
    null
  );
  const [showAdvancedFactorPicker, setShowAdvancedFactorPicker] = useState(false);
  const [factorSegment, setFactorSegment] = useState<FactorSelectorSegment>(() => {
    if (factorSourceChoice === 'predefined') return 'predefined';
    if (factorSourceChoice === 'climatiq') return 'ai-assignment';
    return 'last-used';
  });
  const { data: memorySuggestions = [] } = useMappingMemorySuggestions();
  const [manualFactorId, setManualFactorId] = useState(
    existingEntry?.value.scope === 2
      ? (existingEntry.value.scope2LocationFactor?.emissionFactorId ??
          existingEntry.value.emissionFactorId ??
          '')
      : (existingEntry?.value.emissionFactorId ?? '')
  );
  const [manualFactorSelectorSource, setManualFactorSelectorSource] = useState<
    EmissionFactorSelectorSource | undefined
  >(
    existingEntry?.value.scope === 2
      ? (existingEntry.value.scope2LocationFactor?.emissionFactorSelectorSource ?? undefined)
      : (existingEntry?.value.emissionFactorSelectorSource ?? undefined)
  );
  const [manualFactorPreview, setManualFactorPreview] = useState<EmissionFactor | undefined>(() => {
    // For collection-sourced entries the factor is a predefined registry entry,
    // not a Climatiq factor, so the standard API lookup would fail.  Build a
    // preview from the stored entry data so the badge renders immediately.
    if (
      mode !== 'edit' ||
      !existingEntry?.value.emissionFactorId ||
      (existingEntry.value.activitySource as string) !== 'collection'
    )
      return undefined;
    const v = existingEntry.value;
    return {
      id: v.emissionFactorId ?? '',
      activityId: '',
      name: v.emissionFactorName ?? v.activityType ?? '',
      description: '',
      category: v.activityCategory ?? '',
      sector: '',
      source: v.emissionFactorSource ?? '',
      sourceDataset: '',
      region: '',
      regionName: '',
      year: 0,
      unit: v.emissionFactorRawUnit ?? v.emissionFactorActivityUnit ?? v.unit ?? '',
      unitType: v.emissionFactorUnitType ?? v.activityMethod ?? '',
      co2ePerUnit: v.emissionFactorValue ?? 0,
      calculationMethod: '',
      dataQualityFlags: [],
      accessType: 'public' as const,
      selectorSource: 'climatiq' as const,
    };
  });
  const [showScope2MarketFactorPicker, setShowScope2MarketFactorPicker] = useState(false);
  const [scope2MarketManualFactorId, setScope2MarketManualFactorId] = useState(
    existingEntry?.value.scope2MarketFactor?.emissionFactorId ?? ''
  );
  const [scope2MarketManualFactorSelectorSource, setScope2MarketManualFactorSelectorSource] =
    useState<EmissionFactorSelectorSource | undefined>(
      existingEntry?.value.scope2MarketFactor?.emissionFactorSelectorSource ?? undefined
    );
  const lastAutoCalculationMethodRef = useRef('');
  const [copiedFactorId, setCopiedFactorId] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentCandidate, setAssignmentCandidate] = useState<AssignmentCandidate | null>(null);
  const [assignmentAlternatives, setAssignmentAlternatives] = useState<AlternativeCandidate[]>([]);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [assignmentAutopilotFactor, setAssignmentAutopilotFactor] =
    useState<ActivityAutopilotEmissionFactorSummary | null>(null);
  const [assignmentAutopilotSuggestionId, setAssignmentAutopilotSuggestionId] =
    useState<string | null>(null);
  const [aiAssignedChoice, setAiAssignedChoice] = useState<ActivityChoice | null>(null);
  const [confirmedAssignmentSignature, setConfirmedAssignmentSignature] = useState('');
  const [confirmedAssignmentActivityId, setConfirmedAssignmentActivityId] = useState('');
  const [failedAssignmentSignature, setFailedAssignmentSignature] = useState('');
  const [autopilotDialogOpen, setAutopilotDialogOpen] = useState(false);
  const [autopilotSuggestion, setAutopilotSuggestion] =
    useState<ActivityAutopilotSuggestionItem | null>(null);
  const [autopilotSuggestions, setAutopilotSuggestions] = useState<
    ActivityAutopilotSuggestionItem[]
  >([]);
  const [autopilotSuggestionDataVersion, setAutopilotSuggestionDataVersion] = useState('');
  const [confirmedAutopilotSuggestion, setConfirmedAutopilotSuggestion] =
    useState<ActivityAutopilotSuggestionItem | null>(null);
  const [confirmedAutopilotMethod, setConfirmedAutopilotMethod] = useState('');
  const [confirmedAutopilotDataVersion, setConfirmedAutopilotDataVersion] = useState('');
  const [confirmedAutopilotSignature, setConfirmedAutopilotSignature] = useState('');
  const [failedAutopilotSignature, setFailedAutopilotSignature] = useState('');
  const [emptyAutopilotSignature, setEmptyAutopilotSignature] = useState('');
  const [llmUnavailable, setLlmUnavailable] = useState(false);
  const [wasteCodeInput, setWasteCodeInput] = useState('');
  const [selectedWasteCodeTreatmentId, setSelectedWasteCodeTreatmentId] = useState('');
  const existingWasteCodeParameters =
    existingEntry?.value.emissionFactorParameters &&
    typeof existingEntry.value.emissionFactorParameters === 'object'
      ? existingEntry.value.emissionFactorParameters
      : null;
  const existingWasteCodeInput =
    asStringRecordValue(existingWasteCodeParameters, 'ewcCodeInput') ||
    asStringRecordValue(existingWasteCodeParameters, 'ewcCodeCanonical');
  const existingWasteCodeActivityId = asStringRecordValue(
    existingWasteCodeParameters,
    'wasteCodeActivityId'
  );
  const existingWasteCodeTreatment = asStringRecordValue(
    existingWasteCodeParameters,
    'wasteCodeTreatment'
  );

  const watchedScope = Number(form.watch('scope')) as GHGScope;
  const watchedFacilityId = form.watch('facilityId');
  const watchedActivityCategory = form.watch('activityCategory');
  const watchedCalculationMethod = form.watch('calculationMethod');
  const watchedActivityMethod = form.watch('activityMethod');
  const watchedActivityType = form.watch('activityType');
  const watchedActivityDescription = form.watch('activityDescription');
  const watchedScope3Category = form.watch('scope3Category');
  const watchedReportingPeriodId = form.watch('reportingPeriodId');
  const watchedScope2Country = form.watch('scope2Country');
  const watchedScope2EnergyType = form.watch('scope2EnergyType');
  const watchedScope2MarketInputMode = form.watch('scope2MarketInputMode') as Scope2MarketInputMode;
  const watchedScope2Supplier = form.watch('scope2Supplier');

  // Clear manual factor when unit type changes so auto-resolve can re-evaluate
  const prevMethodRef = useRef(watchedActivityMethod);
  useEffect(() => {
    if (prevMethodRef.current && prevMethodRef.current !== watchedActivityMethod && manualFactorId) {
      setManualFactorId('');
      setManualFactorSelectorSource(undefined);
      setManualFactorPreview(undefined);
    }
    prevMethodRef.current = watchedActivityMethod;
  }, [watchedActivityMethod]); // eslint-disable-line react-hooks/exhaustive-deps
  const watchedScope2MarketEmissionsKg = form.watch('scope2MarketEmissionsKg');
  const watchedScope2LegacyMarketFactorKgCo2ePerKwh = form.watch(
    'scope2LegacyMarketFactorKgCo2ePerKwh'
  );
  const showScope3Category = watchedScope === 3;
  const showScope2Context = watchedScope === 2;
  const normalizedScope3Category = watchedScope3Category.trim();
  const showScope3WasteCodeFlow =
    watchedScope === 3 &&
    watchedActivityCategory.trim().toLowerCase() === 'waste_operations' &&
    normalizedScope3Category === '5' &&
    watchedCalculationMethod.trim() === 'bdo_codes_based';
  const deferredWasteCode = useDeferredValue(wasteCodeInput.trim());
  const wasteCodeResolutionQuery = useResolveScope3WasteCode(deferredWasteCode, {
    enabled: createStep === 2 && showScope3WasteCodeFlow,
  });
  const matchedWasteCodeEntry = useMemo(
    () => getMatchedWasteCodeEntry(wasteCodeResolutionQuery.data),
    [wasteCodeResolutionQuery.data]
  );
  const selectedWasteCodeTreatment = useMemo(
    () => getMatchedWasteCodeTreatment(matchedWasteCodeEntry, selectedWasteCodeTreatmentId),
    [matchedWasteCodeEntry, selectedWasteCodeTreatmentId]
  );
  const wasteCodeResolutionMessage = useMemo(
    () => buildWasteCodeResolutionMessage(wasteCodeResolutionQuery.data),
    [wasteCodeResolutionQuery.data]
  );
  const scope3AutopilotCategory = isScope3AutopilotCategory(normalizedScope3Category)
    ? Number(normalizedScope3Category)
    : null;
  const showScope3AutopilotAssist =
    watchedScope === 3 && scope3AutopilotCategory != null && !showScope3WasteCodeFlow;
  const derivedScope2EnergyType =
    watchedScope === 2 ? defaultScope2EnergyTypeForCategory(watchedActivityCategory) : '';
  const effectiveScope2EnergyType = normalizeScope2EnergyType(
    derivedScope2EnergyType || watchedScope2EnergyType
  );
  const implicitScope2ActivityType = getImplicitScope2ActivityType(effectiveScope2EnergyType, t);
  const activityDescriptionHelpText =
    watchedScope === 3
      ? t('activityDataEntryForm.help_activity_desc_scope3')
      : implicitScope2ActivityType
        ? t('activityDataEntryForm.help_activity_desc_scope2_implicit')
        : t('activityDataEntryForm.help_activity_desc_scope1');
  const scope2ImplicitActivityMessage = implicitScope2ActivityType
    ? t('activityDataEntryForm.help_scope2_implicit_message', { activityType: implicitScope2ActivityType })
    : '';
  const scope2ImplicitActivityContextNote = implicitScope2ActivityType
    ? t('activityDataEntryForm.help_scope2_implicit_context', { activityType: implicitScope2ActivityType })
    : '';
  const showScope2SupplierField =
    showScope2Context &&
    watchedScope2MarketInputMode === 'market_factor' &&
    effectiveScope2EnergyType === 'electricity';

  const hasPeriods = selectablePeriods.length > 0;
  const periodOptions = useMemo(
    () =>
      periodsLoading
        ? [{ value: '', label: 'Loading...' }]
        : selectablePeriods.length === 0
          ? [{ value: '', label: 'No reporting periods available' }]
          : selectablePeriods.map((period) => ({
              value: period.id,
              label: period.label,
            })),
    [periodsLoading, selectablePeriods]
  );

  const facilityOptions = useMemo(
    () =>
      watchedScope === 1 || watchedScope === 2
        ? [
            {
              value: NO_SELECTION,
              label: facilitiesLoading ? 'Loading...' : 'Select a facility',
            },
            ...facilities.map((facility) => ({ value: facility.id, label: facility.name })),
          ]
        : [
            {
              value: NO_SELECTION,
              label: facilitiesLoading ? 'Loading...' : 'No facility',
            },
            ...facilities.map((facility) => ({ value: facility.id, label: facility.name })),
          ],
    [facilities, facilitiesLoading, watchedScope]
  );
  const { gridRegions: allGridRegions, isLoading: allGridRegionsLoading } = useGridRegions();

  const scope2CountryOptions = useMemo(() => {
    const uniqueCountries = new Set<string>();
    for (const country of FALLBACK_SCOPE2_COUNTRY_CODES) {
      uniqueCountries.add(country);
    }
    for (const region of allGridRegions) {
      const code = region.country.trim().toUpperCase();
      if (code.length === 2) uniqueCountries.add(code);
    }
    const currentCountry = watchedScope2Country.trim().toUpperCase();
    if (currentCountry.length === 2) uniqueCountries.add(currentCountry);
    return [...uniqueCountries]
      .sort((a, b) => a.localeCompare(b))
      .map((country) => ({
        value: country,
        label: formatCountryOption(country),
      }));
  }, [allGridRegions, watchedScope2Country]);

  const createCategoryOptions = useMemo(
    () => getCategoryOptionsForScope(createScopeSelection, t),
    [createScopeSelection, t]
  );
  const selectedCreateCategory = useMemo(
    () => createCategoryOptions.find((option) => option.value === createCategorySelection),
    [createCategoryOptions, createCategorySelection]
  );
  const editCategoryOptions = useMemo(() => {
    const options = getCategoryOptionsForScope(
      String(watchedScope) as ActivityScopeOptionValue,
      t
    ).map((option) => ({
      value: resolveSelectionToActivityCategory(watchedScope, option.value),
      label: option.label,
    }));
    if (!watchedActivityCategory) return options;
    const hasCurrent = options.some((option) => option.value === watchedActivityCategory);
    if (hasCurrent) return options;
    return [
      { value: watchedActivityCategory, label: `${watchedActivityCategory} (Current)` },
      ...options,
    ];
  }, [t, watchedActivityCategory, watchedScope]);
  const activityEntryScopeOptions = useMemo(() => getActivityEntryScopeOptions(t), [t]);
  const calculationMethodOptions = useMemo(
    () =>
      watchedActivityCategory
        ? getCalculationMethodOptions({
            scope: watchedScope,
            activityCategory: watchedActivityCategory,
          })
        : [],
    [watchedActivityCategory, watchedScope]
  );
  const allowedActivityMethodsForSelectedCalculationMethod = useMemo(() => {
    if (watchedScope === 2) return null;
    if (mode !== 'create') return null;
    const selectedCalculationMethod = watchedCalculationMethod.trim();
    if (!selectedCalculationMethod || !watchedActivityCategory) return null;
    return getAllowedActivityMethodsForCalculationMethod(
      {
        scope: watchedScope,
        activityCategory: watchedActivityCategory,
      },
      selectedCalculationMethod as ActivityDataCalculationMethod
    );
  }, [mode, watchedActivityCategory, watchedCalculationMethod, watchedScope]);

  const clearScope3AutopilotState = useCallback(
    (options?: { resetActivity?: boolean; resetFactor?: boolean }) => {
      setAutopilotDialogOpen(false);
      setAutopilotSuggestion(null);
      setAutopilotSuggestionDataVersion('');
      setConfirmedAutopilotSuggestion(null);
      setConfirmedAutopilotMethod('');
      setConfirmedAutopilotDataVersion('');
      setConfirmedAutopilotSignature('');
      setFailedAutopilotSignature('');
      setEmptyAutopilotSignature('');

      if (options?.resetActivity) {
        const currentActivityType = form.getValues('activityType');
        if (currentActivityType.startsWith('autopilot:')) {
          form.setValue('activityType', '', { shouldDirty: true, shouldValidate: true });
        }
      }

      if (options?.resetFactor) {
        form.setValue('emissionFactorId', '', { shouldDirty: true, shouldValidate: true });
        setManualFactorId('');
        setManualFactorSelectorSource(undefined);
        setManualFactorPreview(undefined);
      }
    },
    [form]
  );

  const clearInvalidatedActivitySelectionState = useCallback(() => {
    form.setValue('activityMethod', '', { shouldDirty: true, shouldValidate: true });
    form.setValue('activityType', '', { shouldDirty: true, shouldValidate: false });
    form.setValue('emissionFactorId', '', { shouldDirty: true, shouldValidate: true });
    setMethodFormError('');
    setDiscoveredActivity(null);
    setAiAssignedChoice(null);
    setConfirmedAssignmentSignature('');
    setConfirmedAssignmentActivityId('');
    setFailedAssignmentSignature('');
    clearScope3AutopilotState();
    setAssignmentCandidate(null);
    setAssignmentDialogOpen(false);
    setShowAdvancedFactorPicker(false);
    setManualFactorId('');
    setManualFactorSelectorSource(undefined);
    setManualFactorPreview(undefined);
    setShowScope2MarketFactorPicker(false);
    setScope2MarketManualFactorId('');
    setScope2MarketManualFactorSelectorSource(undefined);
    form.setValue('scope2MarketEmissionsKg', '', { shouldDirty: true });
    form.setValue('scope2LegacyMarketFactorKgCo2ePerKwh', '', { shouldDirty: true });
    form.setValue('scope2MarketInputMode', 'same_as_location', { shouldDirty: true });
    form.clearErrors('activityMethod');
    form.clearErrors('activityType');
    form.clearErrors('emissionFactorId');
  }, [clearScope3AutopilotState, form]);

  useEffect(() => {
    if (watchedScope === 2) return;
    const currentValue = form.getValues('calculationMethod').trim();
    const representativeUnit = Object.values(methodUnits).find((value) => value.trim().length > 0);
    const inferredCalculationMethod = inferCalculationMethodForEntry({
      scope: watchedScope,
      activityCategory: watchedActivityCategory,
      activityMethod: watchedActivityMethod,
      unit: representativeUnit ?? null,
    });
    const currentStillAllowed =
      !currentValue || calculationMethodOptions.some((option) => option.value === currentValue);

    if (!currentStillAllowed) {
      form.setValue('calculationMethod', '', { shouldDirty: true, shouldValidate: true });
      lastAutoCalculationMethodRef.current = '';
      return;
    }

    if (!inferredCalculationMethod) {
      if (currentValue && currentValue === lastAutoCalculationMethodRef.current) {
        form.setValue('calculationMethod', '', { shouldDirty: true, shouldValidate: true });
        lastAutoCalculationMethodRef.current = '';
      }
      return;
    }

    if (!calculationMethodOptions.some((option) => option.value === inferredCalculationMethod)) {
      return;
    }

    if (!currentValue || currentValue === lastAutoCalculationMethodRef.current) {
      form.setValue('calculationMethod', inferredCalculationMethod, {
        shouldDirty: currentValue.length > 0,
        shouldValidate: true,
      });
      lastAutoCalculationMethodRef.current = inferredCalculationMethod;
    }
  }, [
    calculationMethodOptions,
    form,
    methodUnits,
    watchedActivityCategory,
    watchedCalculationMethod,
    watchedActivityMethod,
    watchedScope,
  ]);

  useEffect(() => {
    if (watchedScope === 2) return;
    if (mode !== 'create') return;
    const selectedCalculationMethod = watchedCalculationMethod.trim();
    const selectedActivityMethod = watchedActivityMethod.trim();
    if (!selectedCalculationMethod || !selectedActivityMethod) return;
    if (!allowedActivityMethodsForSelectedCalculationMethod) return;
    if (allowedActivityMethodsForSelectedCalculationMethod.has(selectedActivityMethod)) return;
    clearInvalidatedActivitySelectionState();
  }, [
    allowedActivityMethodsForSelectedCalculationMethod,
    clearInvalidatedActivitySelectionState,
    mode,
    watchedActivityMethod,
    watchedCalculationMethod,
    watchedScope,
  ]);

  const selectedCreateScope = Number(createScopeSelection) as GHGScope;
  const resolvedCreateCategory = useMemo(
    () => resolveSelectionToActivityCategory(selectedCreateScope, createCategorySelection),
    [createCategorySelection, selectedCreateScope]
  );
  const defaultScope3Category = useMemo(
    () => defaultScope3CategoryForSelection(selectedCreateScope, createCategorySelection),
    [createCategorySelection, selectedCreateScope]
  );

  useEffect(() => {
    if (!effectiveFixedScope) return;
    const scopeString = String(effectiveFixedScope) as '1' | '2' | '3';
    if (form.getValues('scope') !== scopeString) {
      form.setValue('scope', scopeString, { shouldDirty: true, shouldValidate: true });
    }
    if (createScopeSelection !== scopeString) {
      setCreateScopeSelection(scopeString);
      setCreateCategorySelection(scopeString === '3' ? fixedScope3CategorySelection : '');
    }
  }, [createScopeSelection, effectiveFixedScope, fixedScope3CategorySelection, form]);

  useEffect(() => {
    if (!fixedScope3CategorySelection) return;
    if (createScopeSelection !== '3') return;
    if (createCategorySelection === fixedScope3CategorySelection) return;
    setCreateCategorySelection(fixedScope3CategorySelection);
  }, [createCategorySelection, createScopeSelection, fixedScope3CategorySelection]);

  const catalogMethodsQuery = useActivityCatalogEntries(
    {
      scope: watchedScope as 1 | 2 | 3,
      ...(watchedActivityCategory ? { activityCategory: watchedActivityCategory } : {}),
      ...(watchedScope3Category ? { scope3Category: Number(watchedScope3Category) } : {}),
      includeDiscovered: true,
    },
    {
      enabled: createStep === 2 && Boolean(watchedActivityCategory),
    }
  );

  const catalogEntriesQuery = useActivityCatalogEntries(
    {
      scope: watchedScope as 1 | 2 | 3,
      ...(watchedActivityCategory ? { activityCategory: watchedActivityCategory } : {}),
      ...(watchedScope3Category ? { scope3Category: Number(watchedScope3Category) } : {}),
      ...(watchedActivityMethod ? { unitType: watchedActivityMethod } : {}),
      includeDiscovered: true,
    },
    {
      enabled:
        createStep === 2 && Boolean(watchedActivityCategory) && Boolean(watchedActivityMethod),
    }
  );

  const activityUnitTypeCountsQuery = useActivityUnitTypeCounts(
    {
      scope: watchedScope as 1 | 2 | 3,
      activityCategory: watchedActivityCategory,
      ...(watchedScope3Category ? { scope3Category: Number(watchedScope3Category) } : {}),
    },
    {
      enabled:
        createStep === 2 &&
        (watchedScope === 1 || watchedScope === 2) &&
        Boolean(watchedActivityCategory),
    }
  );

  useEffect(() => {
    if (watchedScope === 3) return;
    const normalizedScope3Category = '';
    const contextKey = `${String(watchedScope)}|${watchedActivityCategory}|${normalizedScope3Category}`;
    if (!watchedActivityCategory) return;
    if (createStep !== 2) return;
    if (
      mode === 'create' &&
      (watchedScope !== selectedCreateScope || watchedActivityCategory !== resolvedCreateCategory)
    ) {
      return;
    }
    if (BOOTSTRAPPED_CONTEXTS.has(contextKey)) return;
    if (ATTEMPTED_BOOTSTRAP_CONTEXTS.has(contextKey)) return;
    if (IN_FLIGHT_BOOTSTRAP_CONTEXTS.has(contextKey)) return;

    ATTEMPTED_BOOTSTRAP_CONTEXTS.add(contextKey);
    IN_FLIGHT_BOOTSTRAP_CONTEXTS.add(contextKey);

    bootstrapMutation.mutate(
      {
        scope: watchedScope as 1 | 2 | 3,
        activityCategory: watchedActivityCategory,
        ...(watchedScope3Category ? { scope3Category: Number(watchedScope3Category) } : {}),
        perMethodLimit: DEFAULT_BOOTSTRAP_PER_METHOD_LIMIT,
      },
      {
        onSuccess: () => {
          BOOTSTRAPPED_CONTEXTS.add(contextKey);
        },
        onSettled: () => {
          IN_FLIGHT_BOOTSTRAP_CONTEXTS.delete(contextKey);
        },
      }
    );
  }, [
    bootstrapMutation,
    createStep,
    mode,
    resolvedCreateCategory,
    selectedCreateScope,
    watchedActivityCategory,
    watchedScope,
    watchedScope3Category,
  ]);

  const curatedActivityChoices = useMemo<ActivityChoice[]>(() => {
    // Scope 1/2 now relies entirely on dynamic catalog discovery.
    if (watchedScope === 1 || watchedScope === 2) {
      return [];
    }
    return getActivitiesFor(watchedScope, watchedActivityCategory).map((activity) => ({
      value: toActivityTypeValue(activity.id),
      label: activity.label,
      activityId: activity.climatiqActivityId,
      activitySource: 'climatiq',
      unitType: getPresetUnitType(activity),
      defaultUnit: activity.defaultUnit,
      source: 'curated',
      presetId: activity.id,
      ...(activity.allowedUnits ? { allowedUnits: activity.allowedUnits } : {}),
      ...(activity.notes ? { notes: activity.notes } : {}),
    }));
  }, [watchedActivityCategory, watchedScope]);

  const discoveredActivityChoice = useMemo<ActivityChoice | null>(() => {
    if (!discoveredActivity) return null;
    return {
      value: DISCOVERED_ACTIVITY_VALUE,
      label: discoveredActivity.displayName,
      activityId: discoveredActivity.activityId,
      activitySource: discoveredActivity.activitySource,
      unitType: discoveredActivity.unitType,
      defaultUnit: discoveredActivity.defaultUnit,
      allowedUnits: [discoveredActivity.defaultUnit],
      notes:
        discoveredActivity.activitySource === 'custom'
          ? 'Selected from organization custom activities'
          : 'Selected from Climatiq discovery',
      source: 'discovered',
    };
  }, [discoveredActivity]);

  const methodActivityCounts = useMemo(() => {
    if (
      (watchedScope === 1 || watchedScope === 2) &&
      activityUnitTypeCountsQuery.data?.unitTypeCounts
    ) {
      const counts = new Map<string, number>();
      for (const [unitType, value] of Object.entries(
        activityUnitTypeCountsQuery.data.unitTypeCounts
      )) {
        if (!isMethodAllowedForScope(watchedScope, unitType)) continue;
        const count = value;
        counts.set(unitType, Number.isFinite(count) && count > 0 ? count : 0);
      }
      return counts;
    }

    const counts = new Map<string, number>();
    const seen = new Set<string>();

    const addCount = (method: string | undefined, activityKey: string | undefined) => {
      if (!method) return;
      if (!isMethodAllowedForScope(watchedScope, method)) return;
      const normalizedActivityKey = activityKey?.trim() ? activityKey : '__unknown__';
      const dedupeKey = `${method}|${normalizedActivityKey}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      counts.set(method, (counts.get(method) ?? 0) + 1);
    };

    for (const choice of curatedActivityChoices) {
      addCount(choice.unitType, choice.activityId || choice.value);
      if (watchedScope === 2 && choice.unitType === 'Energy') {
        addCount('Money', choice.activityId || choice.value);
      }
    }
    for (const entry of catalogMethodsQuery.data?.items ?? []) {
      addCount(entry.unitType, entry.activityId || entry.id);
      if (watchedScope === 2 && entry.unitType === 'Energy') {
        addCount('Money', entry.activityId || entry.id);
      }
    }
    if (discoveredActivity) {
      addCount(discoveredActivity.unitType, discoveredActivity.activityId);
      if (watchedScope === 2 && discoveredActivity.unitType === 'Energy') {
        addCount('Money', discoveredActivity.activityId);
      }
    }

    return counts;
  }, [
    activityUnitTypeCountsQuery.data?.unitTypeCounts,
    catalogMethodsQuery.data?.items,
    curatedActivityChoices,
    discoveredActivity,
    watchedScope,
  ]);

  const methodsWithActivities = useMemo(
    () =>
      [...methodActivityCounts.entries()]
        .filter(([method, count]) => count > 0 && isMethodAllowedForScope(watchedScope, method))
        .map(([method]) => method)
        .sort((a, b) => a.localeCompare(b)),
    [methodActivityCounts, watchedScope]
  );

  const allAvailableMethods = useMemo(() => {
    const methodSet = new Set<string>();
    for (const choice of curatedActivityChoices) {
      if (isMethodAllowedForScope(watchedScope, choice.unitType)) {
        methodSet.add(choice.unitType);
      }
    }
    for (const entry of catalogMethodsQuery.data?.items ?? []) {
      if (entry.unitType && isMethodAllowedForScope(watchedScope, entry.unitType)) {
        methodSet.add(entry.unitType);
      }
    }
    for (const [unitType] of Object.entries(
      activityUnitTypeCountsQuery.data?.unitTypeCounts ?? {}
    )) {
      if (isMethodAllowedForScope(watchedScope, unitType)) {
        methodSet.add(unitType);
      }
    }
    if (
      discoveredActivity?.unitType &&
      isMethodAllowedForScope(watchedScope, discoveredActivity.unitType)
    ) {
      methodSet.add(discoveredActivity.unitType);
    }
    if (watchedScope === 3) {
      for (const method of SUPPORTED_METHODS) methodSet.add(method);
    }

    if (mode === 'edit' && existingEntry?.value.activityMethod) {
      methodSet.add(existingEntry.value.activityMethod);
    }

    return [...methodSet].sort((a, b) => a.localeCompare(b));
  }, [
    activityUnitTypeCountsQuery.data?.unitTypeCounts,
    catalogMethodsQuery.data?.items,
    curatedActivityChoices,
    discoveredActivity?.unitType,
    existingEntry?.value.activityMethod,
    mode,
    watchedScope,
  ]);

  const methodOptions = useMemo(() => {
    const defaultMethods =
      methodsWithActivities.length > 0 ? methodsWithActivities : allAvailableMethods;
    const isCompatibleWithSelectedCalculationMethod = (method: string) =>
      !allowedActivityMethodsForSelectedCalculationMethod ||
      allowedActivityMethodsForSelectedCalculationMethod.has(method);

    const methodSet = new Set(
      defaultMethods.filter((method) => isCompatibleWithSelectedCalculationMethod(method))
    );
    const preferredPresetMethod = scope3TemplatePreset?.activityMethod.trim() ?? '';
    const preferredDefaultMethod =
      defaultCreateActivityMethod?.trim() ??
      getDefaultMethodForCategory(watchedActivityCategory)?.trim() ??
      '';
    if (preferredPresetMethod && isCompatibleWithSelectedCalculationMethod(preferredPresetMethod)) {
      methodSet.add(preferredPresetMethod);
    }
    if (
      preferredDefaultMethod &&
      isCompatibleWithSelectedCalculationMethod(preferredDefaultMethod)
    ) {
      methodSet.add(preferredDefaultMethod);
    }

    // When a calculation method constrains the allowed activity methods (e.g. spend_based -> Money),
    // always include those methods even if no existing activities use them yet.
    if (allowedActivityMethodsForSelectedCalculationMethod) {
      for (const method of allowedActivityMethodsForSelectedCalculationMethod) {
        methodSet.add(method);
      }
    }

    const selectedMethod = watchedActivityMethod.trim();
    if (selectedMethod && isCompatibleWithSelectedCalculationMethod(selectedMethod)) {
      methodSet.add(selectedMethod);
    }
    if (mode === 'edit' && existingEntry?.value.activityMethod) {
      methodSet.add(existingEntry.value.activityMethod);
    }

    return [...methodSet]
      .sort((a, b) => a.localeCompare(b))
      .map((method) => {
        const methodLabel = formatMethodLabel(method);
        if (watchedScope === 3) {
          return { value: method, label: methodLabel };
        }
        return { value: method, label: methodLabel };
      });
  }, [
    allowedActivityMethodsForSelectedCalculationMethod,
    allAvailableMethods,
    defaultCreateActivityMethod,
    existingEntry?.value.activityMethod,
    methodsWithActivities,
    mode,
    scope3TemplatePreset?.activityMethod,
    watchedActivityCategory,
    watchedActivityMethod,
    watchedScope,
  ]);
  const preferredCreateMethod = useMemo(() => {
    const templateMethod = scope3TemplatePreset?.activityMethod.trim() ?? '';
    const defaultMethod =
      defaultCreateActivityMethod?.trim() ??
      getDefaultMethodForCategory(watchedActivityCategory)?.trim() ??
      '';
    const requestedMethod = templateMethod || defaultMethod;
    if (requestedMethod && methodOptions.some((option) => option.value === requestedMethod)) {
      return requestedMethod;
    }
    return methodOptions[0]?.value ?? '';
  }, [
    defaultCreateActivityMethod,
    methodOptions,
    scope3TemplatePreset?.activityMethod,
    watchedActivityCategory,
  ]);

  const orgCatalogChoices = useMemo<ActivityChoice[]>(
    () =>
      (catalogEntriesQuery.data?.items ?? []).map((entry) => ({
        value: `catalog:${entry.id}`,
        label: entry.displayName,
        activityId: entry.activityId,
        activitySource: entry.activitySource ?? 'climatiq',
        unitType: entry.unitType,
        notes: 'Organization default activity',
        source: 'org_catalog',
        ...(entry.defaultUnit ? { defaultUnit: entry.defaultUnit } : {}),
        ...(entry.allowedUnits ? { allowedUnits: entry.allowedUnits } : {}),
      })),
    [catalogEntriesQuery.data?.items]
  );

  const autopilotAssignedChoice = useMemo<ActivityChoice | null>(() => {
    if (!confirmedAutopilotSuggestion) return null;
    return {
      value: buildAutopilotActivityValue(confirmedAutopilotSuggestion.suggestionId),
      label: confirmedAutopilotSuggestion.emissionFactor.name.slice(0, 100),
      activityId: '',
      activitySource: 'climatiq',
      unitType: confirmedAutopilotMethod || watchedActivityMethod || 'Number',
      source: 'autopilot',
      notes: 'Climatiq Autopilot suggestion',
    };
  }, [confirmedAutopilotMethod, confirmedAutopilotSuggestion, watchedActivityMethod]);

  const wasteCodeResolvedChoice = useMemo<ActivityChoice | null>(() => {
    if (!showScope3WasteCodeFlow || !matchedWasteCodeEntry || !selectedWasteCodeTreatment) {
      return null;
    }
    return {
      value: `ewc_mapping:${matchedWasteCodeEntry.canonicalCode}:${selectedWasteCodeTreatment.treatmentId}`,
      label: `${matchedWasteCodeEntry.familyLabel} (${selectedWasteCodeTreatment.label})`,
      activityId: selectedWasteCodeTreatment.activityId,
      activitySource: 'climatiq',
      unitType: watchedActivityMethod || 'Weight',
      source: 'ewc_mapping',
      notes: `Resolved from EWC / BDO code ${matchedWasteCodeEntry.canonicalCode}.`,
    };
  }, [
    matchedWasteCodeEntry,
    selectedWasteCodeTreatment,
    showScope3WasteCodeFlow,
    watchedActivityMethod,
  ]);

  // Base choices — does NOT depend on watchedActivityType.  This is critical:
  // form.setValue('activityType') fires synchronously while setAiAssignedChoice
  // is deferred.  If this memo recomputed on watchedActivityType changes the
  // clearing effect would nuke the value before React applied the new choice.
  const baseActivityChoices = useMemo<ActivityChoice[]>(() => {
    if (showScope3WasteCodeFlow) {
      return wasteCodeResolvedChoice ? [wasteCodeResolvedChoice] : [];
    }

    const filteredCurated = curatedActivityChoices.filter(
      (choice) => choice.unitType === watchedActivityMethod
    );
    const filteredCatalog = orgCatalogChoices.filter(
      (choice) => choice.unitType === watchedActivityMethod
    );

    const merged: ActivityChoice[] = [];
    const appendUnique = (choices: ActivityChoice[]) => {
      for (const choice of choices) {
        const key = choice.activityId.trim() || choice.value;
        if (merged.some((existing) => (existing.activityId.trim() || existing.value) === key)) {
          continue;
        }
        merged.push(choice);
      }
    };

    appendUnique(filteredCatalog);
    if (discoveredActivityChoice?.unitType === watchedActivityMethod) {
      appendUnique([discoveredActivityChoice]);
    }
    // aiAssignedChoice is set by explicit user action (predefined, last-used,
    // autocomplete).  Deduplicate by *value* — not activityId — so it is
    // always selectable even when a catalog entry shares the same activityId
    // but has a different value prefix (e.g. "catalog:uuid" vs "predefined:x").
    if (aiAssignedChoice && aiAssignedChoice.unitType === watchedActivityMethod) {
      if (!merged.some((c) => c.value === aiAssignedChoice.value)) {
        merged.push(aiAssignedChoice);
      }
    }
    if (autopilotAssignedChoice?.unitType === watchedActivityMethod) {
      appendUnique([autopilotAssignedChoice]);
    }
    appendUnique(filteredCurated);

    if (watchedScope === 2 && watchedActivityMethod === 'Money' && merged.length === 0) {
      appendUnique(orgCatalogChoices.filter((choice) => choice.unitType === 'Energy'));
      appendUnique(curatedActivityChoices.filter((choice) => choice.unitType === 'Energy'));
    } else if (
      watchedScope === 2 &&
      watchedActivityMethod === 'Money' &&
      discoveredActivityChoice?.unitType === 'Energy'
    ) {
      appendUnique([discoveredActivityChoice]);
    }

    return merged;
  }, [
    curatedActivityChoices,
    aiAssignedChoice,
    autopilotAssignedChoice,
    discoveredActivityChoice,
    orgCatalogChoices,
    showScope3WasteCodeFlow,
    watchedActivityMethod,
    watchedScope,
    wasteCodeResolvedChoice,
  ]);

  // In edit mode, append a fallback choice for the current value so the form
  // can display an activity that was saved but is no longer in the catalog.
  const activityChoices = useMemo<ActivityChoice[]>(() => {
    if (mode !== 'edit') return baseActivityChoices;

    const currentActivityType = watchedActivityType.trim();
    if (!currentActivityType) return baseActivityChoices;
    if (baseActivityChoices.some((choice) => choice.value === currentActivityType)) {
      return baseActivityChoices;
    }

    // Extract Climatiq activity ID from predefined factor reference or stored parameters
    const fallbackActivityId = currentActivityType.startsWith('predefined:')
      ? currentActivityType.substring('predefined:'.length)
      : asStringRecordValue(existingEntry?.value.emissionFactorParameters, 'activityId');

    return [
      {
        value: currentActivityType,
        label: currentActivityType,
        activityId: fallbackActivityId,
        activitySource: existingEntry?.value.activitySource ?? 'climatiq',
        unitType:
          watchedActivityMethod.trim().length > 0
            ? watchedActivityMethod
            : existingEntry?.value.activityMethod &&
                existingEntry.value.activityMethod.trim().length > 0
              ? existingEntry.value.activityMethod
              : 'Number',
        source: 'existing',
        notes: 'Current activity data value',
      },
      ...baseActivityChoices,
    ];
  }, [
    baseActivityChoices,
    existingEntry?.value.activityMethod,
    existingEntry?.value.activitySource,
    existingEntry?.value.emissionFactorParameters,
    mode,
    watchedActivityMethod,
    watchedActivityType,
  ]);

  const selectedActivityChoice = useMemo(
    () => activityChoices.find((choice) => choice.value === watchedActivityType),
    [activityChoices, watchedActivityType]
  );
  const activityListEmptyForMethod =
    watchedActivityMethod.trim().length > 0 && activityChoices.length === 0;

  const isActivityMethodDirty = Boolean(form.formState.dirtyFields.activityMethod);

  const methodSpec = useMemo(
    () => getParameterSpec(watchedActivityMethod || 'Number'),
    [watchedActivityMethod]
  );

  useEffect(() => {
    if (!watchedActivityMethod) return;

    setMethodValues((previous) => {
      return Object.fromEntries(
        methodSpec.requiredValueKeys.map((key) => [key, previous[key] ?? ''])
      );
    });

    setMethodUnits((previous) => {
      return Object.fromEntries(
        methodSpec.requiredUnitKeys.map((key) => {
          const options = VALID_UNITS[key] ?? [];
          const preferredUnit = getDefaultUnitForCategory(watchedActivityCategory, key);
          const fallbackUnit =
            preferredUnit && options.includes(preferredUnit) ? preferredUnit : (options[0] ?? '');
          return [key, previous[key] ?? fallbackUnit];
        })
      );
    });
  }, [
    methodSpec.requiredUnitKeys,
    methodSpec.requiredValueKeys,
    watchedActivityCategory,
    watchedActivityMethod,
  ]);

  useEffect(() => {
    if (!watchedActivityMethod) return;
    if (mode === 'edit') return;
    const currentActivityType = form.getValues('activityType').trim();
    if (
      currentActivityType &&
      activityChoices.some((choice) => choice.value === currentActivityType)
    ) {
      return;
    }

    // Scope 1/2: don't auto-clear — the unit type onChange handler already
    // resets activityType when the user changes unit type.  Clearing here
    // races with setAiAssignedChoice (deferred) and nukes predefined / AI
    // assignment selections before React applies the new choice.
    if (watchedScope === 1 || watchedScope === 2) {
      return;
    }

    const preferredActivity = activityChoices[0]?.value ?? '';
    if (preferredActivity) {
      if (currentActivityType === preferredActivity) return;
      form.setValue('activityType', preferredActivity, {
        shouldDirty: false,
        shouldValidate: true,
      });
    } else {
      if (currentActivityType === '') return;
      form.setValue('activityType', '', {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [activityChoices, form, mode, watchedActivityMethod, watchedScope]);

  useEffect(() => {
    if (mode !== 'create') return;
    if (createStep !== 2) return;
    if (isActivityMethodDirty && form.getValues('activityMethod').trim() !== '') return;
    const preferredMethod = preferredCreateMethod;
    if (!preferredMethod) return;
    if (form.getValues('activityMethod') === preferredMethod) return;
    form.setValue('activityMethod', preferredMethod, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [createStep, form, isActivityMethodDirty, mode, preferredCreateMethod]);

  useEffect(() => {
    if (watchedScope !== 2) return;
    const derived = defaultScope2EnergyTypeForCategory(watchedActivityCategory);
    if (!derived) return;
    if (form.getValues('scope2EnergyType') === derived) return;
    form.setValue('scope2EnergyType', derived, { shouldValidate: true });
  }, [form, watchedActivityCategory, watchedScope]);

  useEffect(() => {
    if (watchedScope !== 2) return;
    if (form.getValues('scope2AccountingMethod') === 'both') return;
    form.setValue('scope2AccountingMethod', 'both', { shouldValidate: true });
  }, [form, watchedScope]);

  useEffect(() => {
    if (watchedScope !== 2) return;
    if (showScope2SupplierField) return;
    const hasSupplier = form.getValues('scope2Supplier').trim().length > 0;
    if (!hasSupplier) return;
    form.setValue('scope2Supplier', '', { shouldDirty: true, shouldValidate: true });
  }, [form, showScope2SupplierField, watchedScope]);

  useEffect(() => {
    if (!showScope2Context) return;
    if (watchedScope2MarketInputMode === 'market_factor') {
      form.clearErrors('scope2MarketEmissionsKg');
      return;
    }
    if (watchedScope2MarketInputMode === 'manual_total') {
      form.clearErrors('scope2Supplier');
      return;
    }
    form.clearErrors(['scope2Supplier', 'scope2MarketEmissionsKg']);
  }, [form, showScope2Context, watchedScope2MarketInputMode]);

  // Always sync form period with the page-level period selector in create mode
  useEffect(() => {
    if (mode !== 'create') return;
    if (periodId) {
      form.setValue('reportingPeriodId', periodId);
      return;
    }
    if (form.getValues('reportingPeriodId')) return;
    const fallbackPeriodId = selectablePeriods[0]?.id;
    if (!fallbackPeriodId) return;
    form.setValue('reportingPeriodId', fallbackPeriodId);
  }, [form, mode, periodId, selectablePeriods]);

  const preferredUnitForResolution = useMemo(() => {
    const primaryUnitKey = methodSpec.requiredUnitKeys.find((key) => key !== 'time_unit');
    if (!primaryUnitKey) return undefined;
    const unit = methodUnits[primaryUnitKey];
    if (!unit?.trim()) return undefined;
    return unit;
  }, [methodSpec.requiredUnitKeys, methodUnits]);

  const selectedPeriodForForm = useMemo(
    () => selectablePeriods.find((period) => period.id === watchedReportingPeriodId),
    [selectablePeriods, watchedReportingPeriodId]
  );

  const assignmentMethodUnits = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(methodUnits).filter(
          ([key, value]) => key.trim().length > 0 && value.trim().length > 0
        )
      ),
    [methodUnits]
  );

  const assignmentUnit = useMemo(() => {
    const summary = deriveSummaryForMethod({
      activityMethod: watchedActivityMethod || 'Number',
      methodValues: Object.fromEntries(
        Object.entries(methodValues).map(([key, value]) => [key, Number(value) || 0])
      ),
      methodUnits,
    });
    const normalized = summary.unit.trim();
    if (normalized) return normalized;
    if (preferredUnitForResolution?.trim()) return preferredUnitForResolution.trim();
    if (selectedActivityChoice?.defaultUnit?.trim())
      return selectedActivityChoice.defaultUnit.trim();
    return t('activityDataEntryForm.label_unit');
  }, [
    methodUnits,
    methodValues,
    preferredUnitForResolution,
    selectedActivityChoice,
    t,
    watchedActivityMethod,
  ]);

  const scope2ContextForAssignment = useMemo(() => {
    if (watchedScope !== 2 || !effectiveScope2EnergyType) return undefined;
    const normalizedCountry = watchedScope2Country.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(normalizedCountry)) return undefined;
    const normalizedSupplier =
      watchedScope2MarketInputMode === 'market_factor'
        ? watchedScope2Supplier.trim().toLowerCase().replace(/\\s+/g, '_')
        : '';
    return {
      energyType: effectiveScope2EnergyType,
      accountingMethod: 'both' as const,
      country: normalizedCountry,
      ...(normalizedSupplier ? { supplier: normalizedSupplier } : {}),
      ...(watchedScope2MarketInputMode === 'market_factor' &&
      watchedScope2LegacyMarketFactorKgCo2ePerKwh.trim() &&
      Number.isFinite(Number(watchedScope2LegacyMarketFactorKgCo2ePerKwh.trim()))
        ? {
            marketFactorKgCo2ePerKwh: Number(watchedScope2LegacyMarketFactorKgCo2ePerKwh.trim()),
          }
        : {}),
      ...(selectedPeriodForForm?.startDate
        ? { year: new Date(selectedPeriodForForm.startDate).getFullYear() }
        : {}),
    };
  }, [
    effectiveScope2EnergyType,
    selectedPeriodForForm,
    watchedScope,
    watchedScope2Country,
    watchedScope2LegacyMarketFactorKgCo2ePerKwh,
    watchedScope2MarketInputMode,
    watchedScope2Supplier,
  ]);

  const manualFactorSearchContext = useMemo<EmissionFactorSearchContext>(() => {
    const normalizedRegion = watchedScope2Country.trim().toUpperCase();
    const reportingYear = selectedPeriodForForm?.startDate
      ? new Date(selectedPeriodForForm.startDate).getFullYear()
      : undefined;
    const activityLabel = selectedActivityChoice?.label.trim();
    const descriptionQuery = showScope3WasteCodeFlow
      ? (matchedWasteCodeEntry?.familyLabel.trim() ?? '')
      : watchedActivityDescription.trim();
    const normalizedActivityType = watchedActivityType.trim();
    const fallbackQuery =
      activityLabel ?? (normalizedActivityType.length > 0 ? normalizedActivityType : undefined);

    return {
      scope: watchedScope,
      ...(selectedActivityChoice?.activityId.trim()
        ? { activityId: selectedActivityChoice.activityId.trim() }
        : {}),
      ...(activityLabel ? { activityLabel } : {}),
      ...(descriptionQuery
        ? { query: descriptionQuery }
        : fallbackQuery
          ? { query: fallbackQuery }
          : {}),
      ...(/^[A-Z]{2}$/.test(normalizedRegion) ? { region: normalizedRegion } : {}),
      ...(reportingYear ? { year: reportingYear } : {}),
    };
  }, [
    selectedActivityChoice?.activityId,
    selectedActivityChoice?.label,
    selectedPeriodForForm,
    watchedActivityDescription,
    watchedActivityType,
    watchedScope,
    watchedScope2Country,
    matchedWasteCodeEntry?.familyLabel,
    showScope3WasteCodeFlow,
  ]);
  const resolvedClimatiqSource = useMemo(
    () =>
      resolveClimatiqSourceForActivity({
        scope: watchedScope as 1 | 2 | 3,
        ...(watchedActivityCategory ? { activityCategory: watchedActivityCategory } : {}),
        ...(watchedScope3Category.trim() ? { scope3Category: Number(watchedScope3Category) } : {}),
        ...(matchedWasteCodeEntry ? { requestedSource: matchedWasteCodeEntry.source } : {}),
      }),
    [matchedWasteCodeEntry, watchedActivityCategory, watchedScope, watchedScope3Category]
  );
  const manualFactorInitialParams = useMemo<Partial<EmissionFactorSearchParams> | undefined>(
    () => (resolvedClimatiqSource ? { source: resolvedClimatiqSource } : undefined),
    [resolvedClimatiqSource]
  );

  const canAutoAssignActivity =
    (watchedScope === 1 || watchedScope === 2) &&
    watchedActivityCategory.trim().length > 0 &&
    watchedActivityMethod.trim().length > 0 &&
    watchedActivityDescription.trim().length > 0 &&
    assignmentUnit.trim().length > 0;
  const canAutoDetectScope3Activity =
    showScope3AutopilotAssist &&
    watchedActivityCategory.trim().length > 0 &&
    watchedActivityMethod.trim().length > 0 &&
    watchedActivityDescription.trim().length > 0;

  const currentAssignmentSignature = useMemo(
    () =>
      buildAssignmentSignature({
        scope: watchedScope,
        activityCategory: watchedActivityCategory,
        activityMethod: watchedActivityMethod,
        activityDescription: watchedActivityDescription,
      }),
    [watchedActivityCategory, watchedActivityDescription, watchedActivityMethod, watchedScope]
  );
  const currentAutopilotSignature = useMemo(
    () => `${currentAssignmentSignature}|${normalizedScope3Category}`,
    [currentAssignmentSignature, normalizedScope3Category]
  );

  useEffect(() => {
    if (!confirmedAssignmentSignature) return;
    if (confirmedAssignmentSignature === currentAssignmentSignature) return;
    setConfirmedAssignmentSignature('');
    setConfirmedAssignmentActivityId('');
    setAssignmentCandidate(null);
  }, [confirmedAssignmentSignature, currentAssignmentSignature]);

  useEffect(() => {
    if (!confirmedAutopilotSignature) return;
    if (confirmedAutopilotSignature === currentAutopilotSignature) return;
    clearScope3AutopilotState({ resetActivity: true, resetFactor: true });
  }, [clearScope3AutopilotState, confirmedAutopilotSignature, currentAutopilotSignature]);

  useEffect(() => {
    if (!failedAssignmentSignature) return;
    if (failedAssignmentSignature === currentAssignmentSignature) return;
    setFailedAssignmentSignature('');
  }, [currentAssignmentSignature, failedAssignmentSignature]);

  useEffect(() => {
    if (failedAutopilotSignature && failedAutopilotSignature !== currentAutopilotSignature) {
      setFailedAutopilotSignature('');
    }
    if (emptyAutopilotSignature && emptyAutopilotSignature !== currentAutopilotSignature) {
      setEmptyAutopilotSignature('');
    }
  }, [currentAutopilotSignature, emptyAutopilotSignature, failedAutopilotSignature]);

  useEffect(() => {
    if (showScope3WasteCodeFlow) return;
    if (!wasteCodeInput && !selectedWasteCodeTreatmentId) return;
    setWasteCodeInput('');
    setSelectedWasteCodeTreatmentId('');
  }, [selectedWasteCodeTreatmentId, showScope3WasteCodeFlow, wasteCodeInput]);

  useEffect(() => {
    if (!showScope3WasteCodeFlow) return;
    const options = matchedWasteCodeEntry?.treatmentOptions ?? [];
    if (options.length === 1) {
      const onlyOption = options[0];
      if (onlyOption && selectedWasteCodeTreatmentId !== onlyOption.treatmentId) {
        setSelectedWasteCodeTreatmentId(onlyOption.treatmentId);
      }
      return;
    }
    if (
      selectedWasteCodeTreatmentId &&
      options.every((option) => option.treatmentId !== selectedWasteCodeTreatmentId)
    ) {
      setSelectedWasteCodeTreatmentId('');
    }
  }, [matchedWasteCodeEntry, selectedWasteCodeTreatmentId, showScope3WasteCodeFlow]);

  useEffect(() => {
    if (!showScope3WasteCodeFlow) return;
    if (!existingWasteCodeInput || wasteCodeInput.trim()) return;
    setWasteCodeInput(existingWasteCodeInput);
  }, [existingWasteCodeInput, showScope3WasteCodeFlow, wasteCodeInput]);

  useEffect(() => {
    if (!showScope3WasteCodeFlow) return;
    if (selectedWasteCodeTreatmentId || !matchedWasteCodeEntry) return;
    const matchedTreatment = matchedWasteCodeEntry.treatmentOptions.find(
      (option) =>
        option.activityId === existingWasteCodeActivityId ||
        option.label === existingWasteCodeTreatment
    );
    if (matchedTreatment) {
      setSelectedWasteCodeTreatmentId(matchedTreatment.treatmentId);
    }
  }, [
    existingWasteCodeActivityId,
    existingWasteCodeTreatment,
    matchedWasteCodeEntry,
    selectedWasteCodeTreatmentId,
    showScope3WasteCodeFlow,
  ]);

  const scope2PreviewRequest = useMemo(() => {
    if (!showScope2Context) return null;
    if ((mode === 'create' && createStep !== 2) || !watchedActivityMethod) return null;
    if (!isMethodAllowedForScope(2, watchedActivityMethod)) return null;
    if (!effectiveScope2EnergyType) return null;

    const normalizedCountry = watchedScope2Country.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(normalizedCountry)) return null;

    const normalizedAccountingMethod: Scope2AccountingMethod = 'both';

    const normalizedMethodValues: Record<string, number> = {};
    for (const valueKey of methodSpec.requiredValueKeys) {
      const parsed = Number(methodValues[valueKey]);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      normalizedMethodValues[valueKey] = parsed;
    }

    const normalizedMethodUnits: Record<string, string> = {};
    for (const unitKey of methodSpec.requiredUnitKeys) {
      const rawUnit = methodUnits[unitKey]?.trim();
      if (!rawUnit) return null;
      normalizedMethodUnits[unitKey] = rawUnit;
    }

    const normalizedSupplier =
      watchedScope2MarketInputMode === 'market_factor'
        ? watchedScope2Supplier.trim().toLowerCase().replace(/\s+/g, '_')
        : '';
    const parsedMarketEmissions =
      watchedScope2MarketInputMode === 'manual_total' && watchedScope2MarketEmissionsKg.trim()
        ? Number(watchedScope2MarketEmissionsKg.trim())
        : null;
    if (
      parsedMarketEmissions != null &&
      (!Number.isFinite(parsedMarketEmissions) || parsedMarketEmissions <= 0)
    ) {
      return null;
    }
    const selectedPeriod = selectablePeriods.find(
      (period) => period.id === watchedReportingPeriodId
    );
    const scope2LocationFactor: FactorSelectionInput = {
      ...(manualFactorId.trim() ? { emissionFactorId: manualFactorId.trim() } : {}),
      ...(manualFactorSelectorSource
        ? { emissionFactorSelectorSource: manualFactorSelectorSource }
        : {}),
      factorSelectionMode: manualFactorId.trim() ? 'manual' : 'auto',
    };
    const scope2MarketFactor: FactorSelectionInput | undefined =
      watchedScope2MarketInputMode === 'market_factor'
        ? {
            ...(scope2MarketManualFactorId.trim()
              ? { emissionFactorId: scope2MarketManualFactorId.trim() }
              : {}),
            ...(scope2MarketManualFactorSelectorSource
              ? { emissionFactorSelectorSource: scope2MarketManualFactorSelectorSource }
              : {}),
            factorSelectionMode: scope2MarketManualFactorId.trim() ? 'manual' : 'auto',
          }
        : undefined;

    return {
      scope: 2 as const,
      activityMethod: watchedActivityMethod,
      activityCategory: watchedActivityCategory || undefined,
      methodValues: normalizedMethodValues,
      methodUnits: normalizedMethodUnits,
      reportingPeriodId: watchedReportingPeriodId || undefined,
      scope2Context: {
        energyType: effectiveScope2EnergyType,
        accountingMethod: normalizedAccountingMethod,
        country: normalizedCountry,
        ...(normalizedSupplier ? { supplier: normalizedSupplier } : {}),
        ...(watchedScope2MarketInputMode === 'market_factor' &&
        watchedScope2LegacyMarketFactorKgCo2ePerKwh.trim()
          ? {
              marketFactorKgCo2ePerKwh: Number(watchedScope2LegacyMarketFactorKgCo2ePerKwh.trim()),
            }
          : {}),
        ...(selectedPeriod?.startDate
          ? { year: new Date(selectedPeriod.startDate).getFullYear() }
          : {}),
      },
      scope2LocationFactor,
      ...(scope2MarketFactor ? { scope2MarketFactor } : {}),
      ...(parsedMarketEmissions != null ? { scope2MarketEmissionsKg: parsedMarketEmissions } : {}),
    };
  }, [
    createStep,
    effectiveScope2EnergyType,
    manualFactorId,
    manualFactorSelectorSource,
    methodSpec.requiredUnitKeys,
    methodSpec.requiredValueKeys,
    methodUnits,
    methodValues,
    mode,
    showScope2Context,
    scope2MarketManualFactorId,
    scope2MarketManualFactorSelectorSource,
    watchedActivityCategory,
    watchedActivityMethod,
    selectablePeriods,
    watchedReportingPeriodId,
    watchedScope2Country,
    watchedScope2MarketEmissionsKg,
    watchedScope2MarketInputMode,
    watchedScope2LegacyMarketFactorKgCo2ePerKwh,
    watchedScope2Supplier,
  ]);

  const scope2PreviewQuery = useActivityEstimatePreview(scope2PreviewRequest, {
    enabled: showScope2Context,
  });

  const scope2PreviewSummary = useMemo(() => {
    if (!scope2PreviewRequest) return null;
    return deriveSummaryForMethod({
      activityMethod: scope2PreviewRequest.activityMethod,
      methodValues: scope2PreviewRequest.methodValues,
      methodUnits: scope2PreviewRequest.methodUnits,
    });
  }, [scope2PreviewRequest]);

  const scope2PreviewBlockedReason = useMemo(() => {
    if (!showScope2Context) return '';
    if (mode === 'create' && createStep !== 2) {
      return 'Continue to Step 2 to preview Scope 2 factor details.';
    }
    if (!watchedActivityMethod) return 'Select a unit type to preview Scope 2 emissions.';
    if (!isMethodAllowedForScope(2, watchedActivityMethod)) {
      return 'Scope 2 supports only Energy or Money unit types.';
    }
    if (!watchedScope2Country.trim()) return 'Select a country to preview Scope 2 emissions.';
    if (watchedScope2MarketInputMode === 'manual_total' && watchedScope2MarketEmissionsKg.trim()) {
      const parsed = Number(watchedScope2MarketEmissionsKg.trim());
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return 'Market emissions must be greater than 0.';
      }
    }
    for (const valueKey of methodSpec.requiredValueKeys) {
      const parsed = Number(methodValues[valueKey]);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return `Enter ${VALUE_KEY_LABELS[valueKey] ?? valueKey} to preview emissions.`;
      }
    }
    for (const unitKey of methodSpec.requiredUnitKeys) {
      if (!methodUnits[unitKey]?.trim()) {
        return `Select ${UNIT_KEY_LABELS[unitKey] ?? unitKey} to preview emissions.`;
      }
    }
    if (!scope2PreviewRequest) {
      return 'Complete Scope 2 fields to preview emissions.';
    }
    return '';
  }, [
    createStep,
    methodSpec.requiredUnitKeys,
    methodSpec.requiredValueKeys,
    methodUnits,
    methodValues,
    mode,
    scope2PreviewRequest,
    showScope2Context,
    watchedActivityMethod,
    watchedScope2Country,
    watchedScope2MarketEmissionsKg,
    watchedScope2MarketInputMode,
  ]);

  const resolverRequest = useMemo(() => {
    if ((mode === 'create' && createStep !== 2) || !watchedActivityMethod) return null;
    if (watchedScope === 2) return null;
    if (!selectedActivityChoice?.activityId) return null;
    return {
      activityId: selectedActivityChoice.activityId,
      activitySource: selectedActivityChoice.activitySource,
      ...(watchedActivityCategory ? { activityCategory: watchedActivityCategory } : {}),
      ...(watchedScope3Category.trim() ? { scope3Category: Number(watchedScope3Category) } : {}),
      unitType: watchedActivityMethod,
      unit: preferredUnitForResolution,
      methodUnits,
      facilityId: watchedFacilityId !== NO_SELECTION ? watchedFacilityId : undefined,
      reportingPeriodId: watchedReportingPeriodId || undefined,
      ...(resolvedClimatiqSource ? { source: resolvedClimatiqSource } : {}),
      maxCandidates: 8,
    };
  }, [
    createStep,
    methodUnits,
    mode,
    preferredUnitForResolution,
    resolvedClimatiqSource,
    selectedActivityChoice,
    watchedActivityCategory,
    watchedActivityMethod,
    watchedFacilityId,
    watchedReportingPeriodId,
    watchedScope,
    watchedScope3Category,
  ]);

  const resolveFactorQuery = useResolveFactor(resolverRequest, {
    enabled: createStep === 2,
  });

  const assignmentResolverRequest = useMemo(() => {
    if (!assignmentDialogOpen || !assignmentCandidate) return null;
    if ((mode === 'create' && createStep !== 2) || !watchedActivityMethod) return null;

    return {
      activityId: assignmentCandidate.activityId,
      activitySource: 'climatiq' as const,
      ...(watchedActivityCategory ? { activityCategory: watchedActivityCategory } : {}),
      ...(watchedScope3Category.trim() ? { scope3Category: Number(watchedScope3Category) } : {}),
      unitType: assignmentCandidate.unitType || watchedActivityMethod,
      unit: assignmentUnit,
      methodUnits: assignmentMethodUnits,
      facilityId: watchedFacilityId !== NO_SELECTION ? watchedFacilityId : undefined,
      reportingPeriodId: watchedReportingPeriodId || undefined,
      ...(resolvedClimatiqSource ? { source: resolvedClimatiqSource } : {}),
      maxCandidates: 8,
    };
  }, [
    assignmentCandidate,
    assignmentDialogOpen,
    assignmentMethodUnits,
    assignmentUnit,
    createStep,
    mode,
    resolvedClimatiqSource,
    watchedActivityCategory,
    watchedActivityMethod,
    watchedFacilityId,
    watchedReportingPeriodId,
    watchedScope3Category,
  ]);

  const assignmentResolveFactorQuery = useResolveFactor(assignmentResolverRequest, {
    enabled: assignmentDialogOpen,
  });

  const autopilotEstimateParameters = useMemo(
    () =>
      buildAutopilotEstimateParameters({
        methodValues,
        methodUnits,
        requiredValueKeys: methodSpec.requiredValueKeys,
        requiredUnitKeys: methodSpec.requiredUnitKeys,
      }),
    [methodSpec.requiredUnitKeys, methodSpec.requiredValueKeys, methodUnits, methodValues]
  );

  const autopilotEstimateRequest = useMemo(() => {
    // Scope 3: use confirmedAutopilotSuggestion from Scope 3 autopilot dialog
    if (watchedScope === 3 && confirmedAutopilotSuggestion && showScope3AutopilotAssist) {
      if (!confirmedAutopilotSignature || confirmedAutopilotSignature !== currentAutopilotSignature) {
        return null;
      }
      if (!autopilotEstimateParameters) return null;
      return {
        suggestionId: confirmedAutopilotSuggestion.suggestionId,
        parameters: autopilotEstimateParameters,
      };
    }
    // Scope 1/2: use assignmentAutopilotSuggestionId from assignment confirm dialog
    if ((watchedScope === 1 || watchedScope === 2) && assignmentAutopilotSuggestionId && confirmedAssignmentSignature) {
      if (!autopilotEstimateParameters) return null;
      return {
        suggestionId: assignmentAutopilotSuggestionId,
        parameters: autopilotEstimateParameters,
      };
    }
    return null;
  }, [
    assignmentAutopilotSuggestionId,
    autopilotEstimateParameters,
    confirmedAssignmentSignature,
    confirmedAutopilotSignature,
    confirmedAutopilotSuggestion,
    currentAutopilotSignature,
    showScope3AutopilotAssist,
    watchedScope,
  ]);

  const autopilotEstimateQuery = useActivityAutopilotEstimate(autopilotEstimateRequest, {
    enabled: createStep === 2,
  });

  const isCollectionFactor =
    (existingEntry?.value.activitySource as string) === 'collection';
  const manualFactorQuery = useEmissionFactor(manualFactorId, {
    enabled:
      Boolean(manualFactorId) &&
      manualFactorSelectorSource !== 'custom' &&
      !isCollectionFactor,
  });

  const manualCandidate = useMemo(
    () =>
      toResolvedCandidateFromManualFactor(
        manualFactorPreview
          ? {
              id: manualFactorPreview.id,
              activityId: manualFactorPreview.activityId,
              name: manualFactorPreview.name,
              source: manualFactorPreview.source,
              region: manualFactorPreview.region,
              regionName: manualFactorPreview.regionName,
              year: manualFactorPreview.year,
              unit: manualFactorPreview.unit,
              unitType: manualFactorPreview.unitType,
              co2ePerUnit: manualFactorPreview.co2ePerUnit,
              selectorSource: manualFactorPreview.selectorSource,
            }
          : manualFactorQuery.data
            ? {
                id: manualFactorQuery.data.id,
                activityId: manualFactorQuery.data.activityId,
                name: manualFactorQuery.data.name,
                source: manualFactorQuery.data.source,
                region: manualFactorQuery.data.region,
                regionName: manualFactorQuery.data.regionName,
                year: manualFactorQuery.data.year,
                unit: manualFactorQuery.data.unit,
                unitType: manualFactorQuery.data.unitType,
                co2ePerUnit: manualFactorQuery.data.co2ePerUnit,
                selectorSource: manualFactorQuery.data.selectorSource,
              }
            : undefined,
        manualFactorQuery.data?.cachedAt
          ? 'cached'
          : manualFactorPreview?.selectorSource === 'custom'
            ? 'custom'
            : (resolveFactorQuery.data?.resolutionMeta.dataVersion ?? '')
      ),
    [
      manualFactorPreview,
      manualFactorQuery.data,
      resolveFactorQuery.data?.resolutionMeta.dataVersion,
    ]
  );

  const autopilotAutoCandidate = useMemo(
    () =>
      (confirmedAutopilotSuggestion || assignmentAutopilotSuggestionId) && autopilotEstimateQuery.data
        ? mapAutopilotEstimateToResolvedCandidate({
            response: autopilotEstimateQuery.data,
            unitType: confirmedAutopilotMethod || watchedActivityMethod,
            dataVersion: confirmedAutopilotDataVersion,
          })
        : undefined,
    [
      assignmentAutopilotSuggestionId,
      confirmedAutopilotDataVersion,
      autopilotEstimateQuery.data,
      confirmedAutopilotMethod,
      confirmedAutopilotSuggestion,
      watchedActivityMethod,
    ]
  );

  const autoCandidate =
    (watchedScope === 3 && confirmedAutopilotSuggestion) || ((watchedScope === 1 || watchedScope === 2) && assignmentAutopilotSuggestionId && confirmedAssignmentSignature)
      ? autopilotAutoCandidate
      : resolveFactorQuery.data?.selectedFactor;
  const selectedFactorCandidate = manualFactorId ? manualCandidate : autoCandidate;
  const selectedFactorId = manualFactorId.trim() ? manualFactorId : (autoCandidate?.id ?? '');
  const selectedFactorSelectorSource: EmissionFactorSelectorSource | undefined = manualFactorId
    ? (manualFactorSelectorSource ?? manualCandidate?.selectorSource ?? 'climatiq')
    : (autoCandidate?.selectorSource ?? 'climatiq');
  const selectedMode: FactorSelectionMode | undefined = manualFactorId
    ? 'manual'
    : autoCandidate
      ? 'auto'
      : undefined;
  const factorReason = useMemo(() => {
    if (!selectedFactorCandidate) return '';
    if (selectedMode === 'manual') {
      if (showScope3WasteCodeFlow && matchedWasteCodeEntry && selectedWasteCodeTreatment) {
        return `Manual override selected for EWC / BDO code ${matchedWasteCodeEntry.canonicalCode}.`;
      }
      return 'Selected manually from factor search results.';
    }
    if (showScope3WasteCodeFlow && matchedWasteCodeEntry && selectedWasteCodeTreatment) {
      return `Resolved from EWC / BDO code ${matchedWasteCodeEntry.canonicalCode} using ${matchedWasteCodeEntry.source} ${selectedWasteCodeTreatment.label}.`;
    }
    if (confirmedAutopilotSuggestion || assignmentAutopilotSuggestionId) {
      const factorName = confirmedAutopilotSuggestion?.emissionFactor.name ?? selectedFactorCandidate.name;
      return `Resolved via Climatiq Autopilot for ${factorName} (${selectedFactorCandidate.regionName}, ${String(selectedFactorCandidate.year)}).`;
    }
    const activityLabel = selectedActivityChoice?.label ?? selectedFactorCandidate.name;
    const unitType = watchedActivityMethod || selectedFactorCandidate.unitType;
    const region = selectedFactorCandidate.regionName;
    return `Matched automatically for ${activityLabel} using ${unitType} unit type in ${region} (${String(selectedFactorCandidate.year)}).`;
  }, [
    confirmedAutopilotSuggestion,
    matchedWasteCodeEntry,
    selectedActivityChoice?.label,
    selectedFactorCandidate,
    selectedMode,
    selectedWasteCodeTreatment,
    showScope3WasteCodeFlow,
    watchedActivityMethod,
    watchedScope,
  ]);

  const factorSelectionPayload = useMemo(() => {
    if (!selectedFactorId || !selectedMode) {
      return buildFactorSelectionPayload({});
    }

    const payloadInput: Parameters<typeof buildFactorSelectionPayload>[0] = {
      selectedFactorId,
      selectedSelectorSource: selectedFactorSelectorSource,
      selectionMode: selectedMode,
    };

    if (showScope3WasteCodeFlow && matchedWasteCodeEntry && selectedWasteCodeTreatment) {
      payloadInput.selector = {
        ...(manualFactorId.trim() ? {} : { activitySelectionSource: 'ewc_mapping' }),
        activityId: selectedWasteCodeTreatment.activityId,
        activitySource: 'climatiq',
        activityMethod: watchedActivityMethod,
        ewcCodeInput: wasteCodeInput.trim(),
        ewcCodeCanonical: matchedWasteCodeEntry.canonicalCode,
        ewcHazardous: matchedWasteCodeEntry.hazardous,
        wasteCodeSource: matchedWasteCodeEntry.source,
        wasteCodeSourceLabel: matchedWasteCodeEntry.sourceLabel,
        wasteCodeTreatment: selectedWasteCodeTreatment.label,
        wasteCodeActivityId: selectedWasteCodeTreatment.activityId,
        mappingVersion: wasteCodeResolutionQuery.data?.manifestVersion,
      };
    } else if (selectedActivityChoice?.activityId) {
      payloadInput.selector = {
        activityId: selectedActivityChoice.activityId,
        activitySource: selectedActivityChoice.activitySource,
        activitySelectionSource: selectedActivityChoice.source,
        activityMethod: watchedActivityMethod,
        ...(selectedActivityChoice.presetId
          ? { activityPresetId: selectedActivityChoice.presetId }
          : {}),
      };
    } else if (selectedActivityChoice?.source === 'autopilot' && confirmedAutopilotSuggestion) {
      payloadInput.selector = {
        activitySelectionSource: 'autopilot',
        activityMethod: watchedActivityMethod,
        autopilotSuggestionId: confirmedAutopilotSuggestion.suggestionId,
        autopilotLabel: confirmedAutopilotSuggestion.label,
      };
    }

    if (resolveFactorQuery.data) {
      payloadInput.resolution = {
        resolution: resolveFactorQuery.data.resolutionMeta,
      };
    } else if (autopilotEstimateQuery.data) {
      const effectiveSuggestionId = confirmedAutopilotSuggestion?.suggestionId ?? assignmentAutopilotSuggestionId;
      payloadInput.resolution = {
        ...(confirmedAutopilotDataVersion ? { dataVersion: confirmedAutopilotDataVersion } : {}),
        autopilot: {
          suggestionId: effectiveSuggestionId,
          ...(confirmedAutopilotDataVersion ? { dataVersion: confirmedAutopilotDataVersion } : {}),
          calculationDetails: autopilotEstimateQuery.data.calculationDetails,
          sourceTrail: autopilotEstimateQuery.data.sourceTrail,
        },
      };
    }

    return buildFactorSelectionPayload(payloadInput);
  }, [
    assignmentAutopilotSuggestionId,
    autopilotEstimateQuery.data,
    confirmedAutopilotDataVersion,
    confirmedAutopilotSuggestion,
    manualFactorId,
    matchedWasteCodeEntry,
    resolveFactorQuery.data,
    selectedActivityChoice,
    selectedFactorId,
    selectedFactorSelectorSource,
    selectedMode,
    selectedWasteCodeTreatment,
    showScope3WasteCodeFlow,
    wasteCodeInput,
    wasteCodeResolutionQuery.data?.manifestVersion,
    watchedActivityMethod,
  ]);

  const assignmentFactorCandidate = assignmentResolveFactorQuery.data?.selectedFactor;
  const assignmentFactorMode: FactorSelectionMode | undefined = assignmentFactorCandidate
    ? 'auto'
    : undefined;
  const assignmentFactorReason = useMemo(() => {
    if (!assignmentFactorCandidate || !assignmentCandidate) return '';
    return `Matched automatically for ${assignmentCandidate.displayName} using ${
      assignmentCandidate.unitType || watchedActivityMethod
    } unit type in ${assignmentFactorCandidate.regionName} (${String(assignmentFactorCandidate.year)}).`;
  }, [assignmentCandidate, assignmentFactorCandidate, watchedActivityMethod]);

  const activityPreviewRequest = useMemo(() => {
    if (showScope2Context) return null;
    if ((mode === 'create' && createStep !== 2) || !watchedActivityMethod) return null;

    const normalizedMethodValues: Record<string, number> = {};
    for (const valueKey of methodSpec.requiredValueKeys) {
      const parsed = Number(methodValues[valueKey]);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      normalizedMethodValues[valueKey] = parsed;
    }

    const normalizedMethodUnits: Record<string, string> = {};
    for (const unitKey of methodSpec.requiredUnitKeys) {
      const rawUnit = methodUnits[unitKey]?.trim();
      if (!rawUnit) return null;
      normalizedMethodUnits[unitKey] = rawUnit;
    }

    const resolvedFactorId = manualFactorId.trim() || (selectedFactorCandidate?.id ?? '');
    if (!resolvedFactorId) return null;
    const factorSelectionMode: FactorSelectionMode = manualFactorId.trim() ? 'manual' : 'auto';

    return {
      scope: watchedScope,
      activityMethod: watchedActivityMethod,
      activityCategory: watchedActivityCategory || undefined,
      methodValues: normalizedMethodValues,
      methodUnits: normalizedMethodUnits,
      reportingPeriodId: watchedReportingPeriodId || undefined,
      emissionFactorId: resolvedFactorId,
      emissionFactorSelectorSource:
        factorSelectionPayload.emissionFactorSelectorSource ?? selectedFactorSelectorSource,
      emissionFactorParameters: factorSelectionPayload.emissionFactorParameters ?? undefined,
      factorSelectionMode: factorSelectionPayload.factorSelectionMode ?? factorSelectionMode,
    };
  }, [
    createStep,
    factorSelectionPayload.emissionFactorParameters,
    factorSelectionPayload.emissionFactorSelectorSource,
    factorSelectionPayload.factorSelectionMode,
    manualFactorId,
    methodSpec.requiredUnitKeys,
    methodSpec.requiredValueKeys,
    methodUnits,
    methodValues,
    mode,
    selectedFactorCandidate?.id,
    selectedFactorSelectorSource,
    showScope2Context,
    watchedActivityCategory,
    watchedActivityMethod,
    watchedReportingPeriodId,
    watchedScope,
  ]);

  const activityPreviewQuery = useActivityEstimatePreview(activityPreviewRequest, {
    enabled: !showScope2Context,
  });

  const activityPreviewBlockedReason = useMemo(() => {
    if (showScope2Context) return '';
    if (mode === 'create' && createStep !== 2) {
      return 'Continue to Step 2 to preview emissions.';
    }
    if (!watchedActivityMethod) return 'Select a unit type to preview emissions.';
    for (const valueKey of methodSpec.requiredValueKeys) {
      const parsed = Number(methodValues[valueKey]);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return `Enter ${VALUE_KEY_LABELS[valueKey] ?? valueKey} to preview emissions.`;
      }
    }
    for (const unitKey of methodSpec.requiredUnitKeys) {
      if (!methodUnits[unitKey]?.trim()) {
        return `Select ${UNIT_KEY_LABELS[unitKey] ?? unitKey} to preview emissions.`;
      }
    }
    if (showScope3WasteCodeFlow) {
      if (!wasteCodeInput.trim()) {
        return 'Enter an EWC / BDO waste code to resolve a recommended factor.';
      }
      if (!matchedWasteCodeEntry) {
        return wasteCodeResolutionMessage ?? 'No waste-code mapping matched this input.';
      }
      if (!selectedWasteCodeTreatment) {
        return 'Select a waste treatment method to resolve a recommended factor.';
      }
    }
    if (
      watchedScope === 3 &&
      confirmedAutopilotSuggestion &&
      !manualFactorId.trim() &&
      !selectedFactorCandidate?.id
    ) {
      if (!autopilotEstimateParameters) {
        return 'Complete the quantity fields to resolve the confirmed Climatiq Autopilot factor.';
      }
      if (autopilotEstimateQuery.isLoading || autopilotEstimateQuery.isFetching) {
        return 'Resolving the confirmed Climatiq Autopilot factor.';
      }
      if (autopilotEstimateQuery.error) {
        return 'Climatiq Autopilot could not resolve a factor. Choose one manually to continue.';
      }
    }
    if (!(manualFactorId.trim() || selectedFactorCandidate?.id)) {
      return 'Resolve or choose an emission factor to preview emissions.';
    }
    if (!activityPreviewRequest) {
      return 'Complete required fields to preview emissions.';
    }
    return '';
  }, [
    activityPreviewRequest,
    autopilotEstimateParameters,
    autopilotEstimateQuery.error,
    autopilotEstimateQuery.isFetching,
    autopilotEstimateQuery.isLoading,
    confirmedAutopilotSuggestion,
    createStep,
    manualFactorId,
    methodSpec.requiredUnitKeys,
    methodSpec.requiredValueKeys,
    methodUnits,
    methodValues,
    matchedWasteCodeEntry,
    mode,
    selectedFactorCandidate?.id,
    selectedWasteCodeTreatment,
    showScope2Context,
    showScope3WasteCodeFlow,
    wasteCodeInput,
    wasteCodeResolutionMessage,
    watchedActivityMethod,
    watchedScope,
  ]);

  const isAutopilotFactorResolving =
    !manualFactorId &&
    watchedScope === 3 &&
    Boolean(confirmedAutopilotSuggestion) &&
    (autopilotEstimateQuery.isLoading || autopilotEstimateQuery.isFetching);
  const isAutoFactorResolving =
    !manualFactorId &&
    (isAutopilotFactorResolving ||
      (Boolean(resolverRequest) &&
        (resolveFactorQuery.isLoading || resolveFactorQuery.isFetching)));
  const isManualFactorResolving =
    Boolean(manualFactorId) && (manualFactorQuery.isLoading || manualFactorQuery.isFetching);
  const isFactorResolving = isAutoFactorResolving || isManualFactorResolving;

  const factorBadgeError = useMemo(() => {
    if (isFactorResolving || selectedFactorCandidate) return null;
    if (showScope3WasteCodeFlow) {
      if (!wasteCodeInput.trim()) {
        return 'Enter an EWC / BDO waste code to resolve a recommended factor or choose one manually.';
      }
      if (!matchedWasteCodeEntry) {
        return wasteCodeResolutionMessage ?? 'No waste-code mapping matched this input.';
      }
      if (!selectedWasteCodeTreatment) {
        return 'Select a waste treatment method to resolve a recommended factor.';
      }
      if (resolveFactorQuery.error) {
        return resolveFactorQuery.error.message;
      }
      return 'No factor could be auto-resolved for the selected waste code. Choose a manual factor to continue.';
    }
    if (confirmedAutopilotSuggestion) {
      if (!autopilotEstimateParameters) {
        return 'Complete the quantity fields to resolve the confirmed Climatiq Autopilot factor.';
      }
      if (autopilotEstimateQuery.error) {
        return autopilotEstimateQuery.error.message;
      }
      return 'Climatiq Autopilot did not return a factor. Choose a manual factor to continue.';
    }
    return 'No factor could be auto-resolved. Choose a manual factor to continue.';
  }, [
    autopilotEstimateParameters,
    autopilotEstimateQuery.error,
    confirmedAutopilotSuggestion,
    isFactorResolving,
    matchedWasteCodeEntry,
    resolveFactorQuery.error,
    selectedFactorCandidate,
    selectedWasteCodeTreatment,
    showScope3WasteCodeFlow,
    wasteCodeInput,
    wasteCodeResolutionMessage,
  ]);

  useEffect(() => {
    const nextEmissionFactorId = selectedFactorId;
    const currentEmissionFactorId = form.getValues('emissionFactorId');
    if (currentEmissionFactorId === nextEmissionFactorId) return;
    form.setValue('emissionFactorId', nextEmissionFactorId, { shouldValidate: true });
  }, [form, selectedFactorId]);

  // When the Scope 1/2 Autopilot estimate resolves, update the activity choice
  // with the real activityId and display name from the resolved factor.
  useEffect(() => {
    if (!assignmentAutopilotSuggestionId || !autopilotAutoCandidate || !aiAssignedChoice) return;
    if (watchedScope === 3) return;
    const resolvedActivityId = autopilotAutoCandidate.activityId;
    if (!resolvedActivityId || aiAssignedChoice.activityId === resolvedActivityId) return;
    const updatedLabel = `${autopilotAutoCandidate.name} (${autopilotAutoCandidate.source}, ${autopilotAutoCandidate.regionName || autopilotAutoCandidate.region || 'Global'})`;
    setAiAssignedChoice((prev) =>
      prev
        ? {
            ...prev,
            activityId: resolvedActivityId,
            label: updatedLabel,
            defaultUnit: autopilotAutoCandidate.unit || prev.defaultUnit,
          }
        : prev
    );
  }, [aiAssignedChoice, assignmentAutopilotSuggestionId, autopilotAutoCandidate, watchedScope]);

  useEffect(() => {
    setCopiedFactorId(false);
  }, [selectedFactorId]);

  useEffect(() => {
    if (manualFactorId.trim()) return;
    setManualFactorSelectorSource(undefined);
    setManualFactorPreview(undefined);
  }, [manualFactorId]);

  const handleCancel = () => {
    if (mode === 'create') {
      setCreateStep(1);
      setCreateCategorySelection('');
      setCreateStepError('');
      setMethodFormError('');
      setDiscoveredActivity(null);
      setAiAssignedChoice(null);
      setConfirmedAssignmentSignature('');
      setConfirmedAssignmentActivityId('');
      setFailedAssignmentSignature('');
      clearScope3AutopilotState();
      setAssignmentCandidate(null);
      setShowAdvancedFactorPicker(false);
      setManualFactorId('');
      setWasteCodeInput('');
      setSelectedWasteCodeTreatmentId('');
    }
    onCancel();
  };

  const handleContinueToStep2 = () => {
    if (!createCategorySelection) {
      setCreateStepError(t('activityDataEntryForm.error_select_category_to_continue'));
      return;
    }

    const normalizedCategory = resolveSelectionToActivityCategory(
      selectedCreateScope,
      createCategorySelection
    );
    if (!normalizedCategory) {
      setCreateStepError(t('activityDataEntryForm.error_invalid_category_selection'));
      return;
    }

    form.setValue('scope', createScopeSelection, { shouldDirty: true, shouldValidate: true });
    form.setValue('activityCategory', normalizedCategory, {
      shouldDirty: true,
      shouldValidate: true,
    });
    const scopedTemplatePreset = selectedCreateScope === 3 ? scope3TemplatePreset : undefined;
    const initialMethod = scopedTemplatePreset?.activityMethod.trim() ?? '';
    const initialDescription = scopedTemplatePreset?.activityDescription?.trim() ?? '';
    const initialNotes = scopedTemplatePreset?.notes?.trim() ?? '';

    form.setValue('activityMethod', initialMethod, { shouldDirty: false, shouldValidate: true });
    form.setValue('activityType', '', { shouldDirty: true, shouldValidate: false });
    form.setValue('activityDescription', initialDescription, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue('emissionFactorId', '', { shouldDirty: true, shouldValidate: true });
    form.setValue('notes', initialNotes, { shouldDirty: true, shouldValidate: true });

    if (selectedCreateScope === 2) {
      form.setValue('scope2EnergyType', defaultScope2EnergyTypeForCategory(normalizedCategory), {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope2AccountingMethod', 'both', {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope2Country', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope2RegionCode', NO_SELECTION, {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope2MarketInputMode', 'same_as_location', {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope2Supplier', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope2MarketEmissionsKg', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope2LegacyMarketFactorKgCo2ePerKwh', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope3Category', '', { shouldDirty: true });
    } else if (selectedCreateScope === 3) {
      const scope3Category = defaultScope3CategoryForSelection(
        selectedCreateScope,
        createCategorySelection
      );
      form.setValue('scope3Category', scope3Category ? String(scope3Category) : '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.setValue('scope2EnergyType', '', { shouldDirty: true });
      form.setValue('scope2AccountingMethod', '', { shouldDirty: true });
      form.setValue('scope2Country', '', { shouldDirty: true });
      form.setValue('scope2RegionCode', NO_SELECTION, { shouldDirty: true });
      form.setValue('scope2MarketInputMode', 'same_as_location', { shouldDirty: true });
      form.setValue('scope2Supplier', '', { shouldDirty: true });
      form.setValue('scope2MarketEmissionsKg', '', { shouldDirty: true });
      form.setValue('scope2LegacyMarketFactorKgCo2ePerKwh', '', { shouldDirty: true });
    } else {
      form.setValue('scope2EnergyType', '', { shouldDirty: true });
      form.setValue('scope2AccountingMethod', '', { shouldDirty: true });
      form.setValue('scope2Country', '', { shouldDirty: true });
      form.setValue('scope2RegionCode', NO_SELECTION, { shouldDirty: true });
      form.setValue('scope2MarketInputMode', 'same_as_location', { shouldDirty: true });
      form.setValue('scope2Supplier', '', { shouldDirty: true });
      form.setValue('scope2MarketEmissionsKg', '', { shouldDirty: true });
      form.setValue('scope2LegacyMarketFactorKgCo2ePerKwh', '', { shouldDirty: true });
      form.setValue('scope3Category', '', { shouldDirty: true });
    }

    setMethodValues({ ...(scopedTemplatePreset?.methodValues ?? {}) });
    setMethodUnits({ ...(scopedTemplatePreset?.methodUnits ?? {}) });
    setMethodFormError('');
    setDiscoveredActivity(null);
    setAiAssignedChoice(null);
    setConfirmedAssignmentSignature('');
    setConfirmedAssignmentActivityId('');
    setFailedAssignmentSignature('');
    clearScope3AutopilotState();
    setAssignmentCandidate(null);
    setShowAdvancedFactorPicker(false);
    setManualFactorId('');
    setWasteCodeInput('');
    setSelectedWasteCodeTreatmentId('');
    setScope2MarketManualFactorId('');
    setScope2MarketManualFactorSelectorSource(undefined);
    setShowScope2MarketFactorPicker(false);
    setCreateStepError('');
    setCreateStep(2);
  };

  const handleUseDiscoveredActivity = (candidate: ActivityDiscoveryCandidate) => {
    clearScope3AutopilotState();
    setAssignmentCandidate(null);
    setConfirmedAssignmentSignature('');
    setConfirmedAssignmentActivityId('');
    setFailedAssignmentSignature('');
    form.setValue('activityMethod', candidate.unitType, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const existingChoice = activityChoices.find(
      (choice) => choice.activityId === candidate.activityId && choice.source !== 'discovered'
    );

    if (existingChoice) {
      setDiscoveredActivity(null);
      form.setValue('activityType', existingChoice.value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else {
      setDiscoveredActivity(candidate);
      form.setValue('activityType', DISCOVERED_ACTIVITY_VALUE, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    setManualFactorId('');
    form.clearErrors('activityDescription');
    form.clearErrors('emissionFactorId');

    if (watchedScope !== 3) return;
    if (!watchedActivityCategory || !watchedScope3Category.trim()) return;

    if (candidate.activitySource !== 'climatiq') {
      return;
    }

    promoteActivityMutation.mutate(
      {
        scope: 3,
        activityCategory: watchedActivityCategory,
        scope3Category: Number(watchedScope3Category),
        activityId: candidate.activityId,
        unitType: candidate.unitType,
        displayName: candidate.displayName,
        defaultUnit: candidate.defaultUnit,
        allowedUnits: [candidate.defaultUnit],
        metadata: {
          source: candidate.source,
          sourceDataset: candidate.sourceDataset,
          region: candidate.region,
          year: candidate.year,
          unitType: candidate.unitType,
        },
      },
      {
        onError: () => {
          toast.warning(
            t('activityDataEntryForm.warning_activity_selected_but_promotion_to_catal')
          );
        },
      }
    );
  };

  const handleAutoAssignActivity = async () => {
    if (!canAutoAssignActivity || isApproved || llmUnavailable) return;
    const description = watchedActivityDescription.trim();
    if (!description) {
      form.setError('activityDescription', {
        type: 'custom',
        message: t('validation.activityDescriptionRequiredForAutoAssign'),
      });
      return;
    }

    try {
      const response = await suggestAssignmentMutation.mutateAsync({
        scope: watchedScope,
        activityCategory: watchedActivityCategory.trim(),
        activityMethod: watchedActivityMethod.trim(),
        activityDescription: description,
        reportingPeriodId: watchedReportingPeriodId || undefined,
        facilityId: watchedFacilityId !== NO_SELECTION ? watchedFacilityId : undefined,
        ...(watchedScope === 2 && scope2ContextForAssignment
          ? { scope2Context: scope2ContextForAssignment }
          : {}),
        maxCandidates: 5,
        ...(factorSourceChoice ? { factorSourcePreference: factorSourceChoice } : {}),
      });

      setAssignmentCandidate(response.assignment);
      setAssignmentAlternatives(response.alternatives ?? []);
      setShowAlternatives(false);
      setAssignmentAutopilotFactor(response.autopilotFactor ?? null);
      setAssignmentAutopilotSuggestionId(response.autopilotSuggestionId ?? null);
      setAssignmentDialogOpen(true);
      setFailedAssignmentSignature('');
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 422) {
        const errorCode = (error.details as { code?: string } | undefined)?.code;
        if (errorCode === 'LLM_NOT_CONFIGURED' || errorCode === 'LLM_CONFIG_ERROR') {
          setLlmUnavailable(true);
          setFailedAssignmentSignature(currentAssignmentSignature);
          toast.warning(t('ghg:toast.autoassignIsUnavailableRightNowYouCanStillSubmitWithAManuall'));
          return;
        }
        // For AUTOPILOT_NO_RESULTS and other transient 422s, show warning but allow retry
        toast.warning(
          error.message || t('ghg:toast.autoassignIsUnavailableRightNowYouCanStillSubmitWithAManuall')
        );
        return;
      }
      toast.error(
        error instanceof Error ? error.message : 'Failed to generate assignment candidates.'
      );
    }
  };

  const handleAutoDetectScope3Activity = async () => {
    if (!canAutoDetectScope3Activity || !scope3AutopilotCategory || isApproved) return;
    const description = watchedActivityDescription.trim();
    if (!description) {
      form.setError('activityDescription', {
        type: 'custom',
        message: t('validation.activityDescriptionRequiredForAutoDetect'),
      });
      return;
    }

    try {
      const response = await suggestAutopilotMutation.mutateAsync({
        text: description,
        scope3Category: scope3AutopilotCategory,
        unitType: watchedActivityMethod.trim(),
        maxSuggestions: 5,
      });

      const suggestions = response.results ?? [];
      if (suggestions.length === 0) {
        clearScope3AutopilotState({ resetActivity: true, resetFactor: true });
        setFailedAutopilotSignature('');
        setEmptyAutopilotSignature(currentAutopilotSignature);
        toast.warning(t('ghg:toast.noAutopilotSuggestionWasFoundYouCanContinueWithManualActivit'));
        return;
      }

      setAutopilotSuggestion(suggestions[0]);
      setAutopilotSuggestions(suggestions);
      setAutopilotSuggestionDataVersion(response.dataVersion?.trim() ?? '');
      setAutopilotDialogOpen(true);
      setFailedAutopilotSignature('');
      setEmptyAutopilotSignature('');
    } catch (error) {
      setFailedAutopilotSignature(currentAutopilotSignature);
      setEmptyAutopilotSignature('');
      toast.warning(
        error instanceof Error
          ? error.message
          : 'Autopilot is unavailable right now. You can continue with manual activity search.'
      );
    }
  };

  const renderEmissionFactorCard = (input: {
    name: string;
    source?: string | null | undefined;
    regionName?: string | null | undefined;
    year?: string | number | null | undefined;
    unit?: string | null | undefined;
    reason?: string | null | undefined;
    factorId?: string | null | undefined;
    activityId?: string | null | undefined;
    unitType?: string | null | undefined;
    dataVersion?: string | null | undefined;
  }) => (
    <div className="rounded-md border bg-background p-3">
      <p className="text-sm font-medium">{input.name}</p>
      <p className="text-xs text-muted-foreground">
        {input.source ?? 'Unknown source'} · {input.regionName ?? 'Unknown region'} ·{' '}
        {input.year ?? 'Unknown year'} · {input.unit ?? 'Unknown unit'}
      </p>
      {input.reason && (
        <p className="mt-1 text-xs text-muted-foreground">Why this factor: {input.reason}</p>
      )}
      {input.factorId && (
        <div className="mt-2 flex items-center gap-2">
          <p className="font-mono text-xs text-muted-foreground">
            ID {shortenFactorId(input.factorId)}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => {
              if (typeof navigator === 'undefined') return;
              void navigator.clipboard
                .writeText(String(input.factorId))
                .then(() => {
                  setCopiedFactorId(true);
                  setTimeout(() => {
                    setCopiedFactorId(false);
                  }, 1500);
                })
                .catch(() => {
                  setCopiedFactorId(false);
                });
            }}
            disabled={isApproved}
          >
            {copiedFactorId ? (
              <>
                <Check className="mr-1 h-3 w-3" />
                Copied
              </>
            ) : (
              <>
                <Copy className="mr-1 h-3 w-3" />
                Copy ID
              </>
            )}
          </Button>
        </div>
      )}
      <details className="mt-2 rounded-md border border-dashed px-3 py-2">
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
          Details
        </summary>
        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          {input.factorId && (
            <p>
              <span className="font-medium text-foreground">Factor ID:</span>{' '}
              <span className="font-mono">{input.factorId}</span>
            </p>
          )}
          {input.activityId && (
            <p>
              <span className="font-medium text-foreground">Activity ID:</span>{' '}
              <span className="font-mono">{input.activityId}</span>
            </p>
          )}
          {input.unitType && (
            <p>
              <span className="font-medium text-foreground">Unit type:</span> {input.unitType}
            </p>
          )}
          <p>
            <span className="font-medium text-foreground">Data version:</span>{' '}
            {input.dataVersion ?? 'N/A'}
          </p>
        </div>
      </details>
    </div>
  );

  const handleConfirmAssignmentCandidate = async () => {
    if (watchedScope === 3) return;
    const candidate = assignmentCandidate;
    if (!candidate) return;

    try {
      const response = await confirmAssignmentMutation.mutateAsync({
        scope: watchedScope,
        activityCategory: watchedActivityCategory.trim(),
        activityMethod: watchedActivityMethod.trim(),
        activityDescription: watchedActivityDescription.trim(),
        selectedCatalogEntryId: candidate.catalogEntryId,
        unit: assignmentUnit,
        methodUnits: assignmentMethodUnits,
        reportingPeriodId: watchedReportingPeriodId || undefined,
        facilityId: watchedFacilityId !== NO_SELECTION ? watchedFacilityId : undefined,
        sourceContext: 'manual_entry_form',
        ...(watchedScope === 2 && scope2ContextForAssignment
          ? { scope2Context: scope2ContextForAssignment }
          : {}),
      });

      const nextMethod = response.unitType;
      const candidateActivityId = response.activityId;
      const existingChoice = candidateActivityId.trim()
        ? activityChoices.find(
            (choice) =>
              choice.activityId.trim() === candidateActivityId.trim() && choice.unitType === nextMethod
          )
        : undefined;

      form.setValue('activityMethod', nextMethod, { shouldDirty: true, shouldValidate: true });
      setDiscoveredActivity(null);

      if (existingChoice) {
        setAiAssignedChoice(null);
        form.setValue('activityType', existingChoice.value, {
          shouldDirty: true,
          shouldValidate: true,
        });
      } else {
        const autopilotLabel = assignmentAutopilotFactor
          ? `${assignmentAutopilotFactor.name} (${assignmentAutopilotFactor.source}, ${assignmentAutopilotFactor.region || 'Global'})`
          : response.displayName;
        const injectedChoice: ActivityChoice = {
          value: `catalog:${response.catalogEntryId}`,
          label: autopilotLabel,
          activityId: response.activityId,
          activitySource: 'climatiq',
          unitType: response.unitType,
          defaultUnit: assignmentAutopilotFactor?.unit || undefined,
          source: 'org_catalog',
          notes: 'AI-assigned activity',
        };
        setAiAssignedChoice(injectedChoice);
        form.setValue('activityType', injectedChoice.value, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }

      setManualFactorId('');
      if (response.factorPreview.resolved && response.factorPreview.emissionFactorId) {
        form.setValue('emissionFactorId', response.factorPreview.emissionFactorId, {
          shouldDirty: true,
          shouldValidate: true,
        });
        form.clearErrors('emissionFactorId');
      } else if (assignmentAutopilotSuggestionId) {
        // Factor will be resolved via Autopilot estimate once quantity is entered
        form.clearErrors('emissionFactorId');
      } else {
        form.setValue('emissionFactorId', '', { shouldDirty: true, shouldValidate: true });
        form.setError('emissionFactorId', {
          type: 'custom',
          message: t('validation.noEmissionFactorResolved'),
        });
      }

      setConfirmedAssignmentSignature(currentAssignmentSignature);
      setConfirmedAssignmentActivityId(response.activityId.trim());
      setFailedAssignmentSignature('');
      setAssignmentDialogOpen(false);
      toast.success(t('activityDataEntryForm.success_activity_candidate_confirmed'));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to confirm selected assignment candidate.'
      );
    }
  };

  const handleConfirmAutopilotSuggestion = () => {
    if (!autopilotSuggestion) return;

    const nextChoice = {
      value: buildAutopilotActivityValue(autopilotSuggestion.suggestionId),
      label: autopilotSuggestion.emissionFactor.name.slice(0, 100),
    };

    setDiscoveredActivity(null);
    setAiAssignedChoice(null);
    setConfirmedAutopilotSuggestion(autopilotSuggestion);
    setConfirmedAutopilotMethod(watchedActivityMethod.trim());
    setConfirmedAutopilotDataVersion(autopilotSuggestionDataVersion);
    setConfirmedAutopilotSignature(currentAutopilotSignature);
    setFailedAutopilotSignature('');
    setEmptyAutopilotSignature('');
    setAutopilotDialogOpen(false);
    setAutopilotSuggestion(null);
    setManualFactorId('');
    setManualFactorSelectorSource(undefined);
    setManualFactorPreview(undefined);

    form.setValue('activityType', nextChoice.value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    form.setValue('emissionFactorId', '', { shouldDirty: true, shouldValidate: true });
    form.clearErrors('activityType');
    form.clearErrors('emissionFactorId');

    toast.success(t('activityDataEntryForm.success_activity_suggestion_confirmed'));
  };

  const handleSubmit = async (values: FormValues) => {
    const scope = effectiveFixedScope ?? (Number(values.scope) as GHGScope);
    if ((scope === 1 || scope === 2) && !values.activityDescription.trim()) {
      form.setError('activityDescription', {
        type: 'custom',
        message: t('validation.scope12RequireDescription'),
      });
      return;
    }
    if (
      scope === 2 &&
      !SCOPE2_ALLOWED_METHODS.includes(values.activityMethod as 'Energy' | 'Money')
    ) {
      form.setError('activityMethod', {
        type: 'custom',
        message: t('validation.scope2UnitTypes'),
      });
      return;
    }
    const selectedCalculationMethod = values.calculationMethod.trim();
    if (scope !== 2 && !selectedCalculationMethod) {
      form.setError('calculationMethod', {
        type: 'custom',
        message: t('validation.calculationMethodRequired'),
      });
      return;
    }
    const spec = getParameterSpec(values.activityMethod);

    const normalizedMethodValues: Record<string, number> = {};
    for (const valueKey of spec.requiredValueKeys) {
      const raw = methodValues[valueKey];
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        const msg = `${VALUE_KEY_LABELS[valueKey] ?? valueKey} must be greater than 0.`;
        setMethodFormError(msg);
        toast.error(t('ghg:toast.pleaseFixTheHighlightedFieldsBeforeSubmitting'), {
          description: msg,
        });
        return;
      }
      normalizedMethodValues[valueKey] = parsed;
    }

    const normalizedMethodUnits: Record<string, string> = {};
    for (const unitKey of spec.requiredUnitKeys) {
      const rawUnit = methodUnits[unitKey]?.trim();
      if (!rawUnit) {
        const msg = `${UNIT_KEY_LABELS[unitKey] ?? unitKey} is required.`;
        setMethodFormError(msg);
        toast.error(t('ghg:toast.pleaseFixTheHighlightedFieldsBeforeSubmitting'), {
          description: msg,
        });
        return;
      }
      normalizedMethodUnits[unitKey] = rawUnit;
    }

    setMethodFormError('');

    const scope2EnergyType = normalizeScope2EnergyType(
      defaultScope2EnergyTypeForCategory(values.activityCategory) || values.scope2EnergyType.trim()
    );
    const implicitScope2ActivityType =
      scope === 2 ? getImplicitScope2ActivityType(scope2EnergyType, t) : '';
    const shouldUseScope3ActivityLabel =
      scope === 3 &&
      (selectedActivityChoice?.source === 'discovered' ||
        selectedActivityChoice?.source === 'autopilot' ||
        selectedActivityChoice?.source === 'ewc_mapping');
    let activityTypeForPayload = values.activityType.trim();
    if (shouldUseScope3ActivityLabel) {
      activityTypeForPayload = selectedActivityChoice.label.slice(0, 100);
    }
    if (implicitScope2ActivityType) {
      activityTypeForPayload = implicitScope2ActivityType;
    }

    if ((scope === 1 || (scope === 2 && !implicitScope2ActivityType)) && mode === 'create') {
      const hasManuallySelectedActivity =
        selectedActivityChoice && selectedActivityChoice.activityId.trim().length > 0;
      // Autopilot-confirmed activities have empty activityId but valid confirmedAutopilotSignature
      const hasAutopilotConfirmedActivity = Boolean(confirmedAutopilotSignature);
      if (!hasManuallySelectedActivity && !hasAutopilotConfirmedActivity) {
        const signature = buildAssignmentSignature({
          scope,
          activityCategory: values.activityCategory,
          activityMethod: values.activityMethod,
          activityDescription: values.activityDescription,
        });
        const canSubmitWithoutConfirmedAssignment = failedAssignmentSignature === signature;
        if (
          !canSubmitWithoutConfirmedAssignment &&
          (!confirmedAssignmentSignature ||
            confirmedAssignmentSignature !== signature ||
            !confirmedAssignmentActivityId ||
            selectedActivityChoice?.activityId.trim() !== confirmedAssignmentActivityId)
        ) {
          form.setError('activityDescription', {
            type: 'custom',
            message: t('validation.runAutoAssignFirst'),
          });
          return;
        }
      }
    }
    if ((scope === 1 || scope === 2) && !activityTypeForPayload) {
      const signature = buildAssignmentSignature({
        scope,
        activityCategory: values.activityCategory,
        activityMethod: values.activityMethod,
        activityDescription: values.activityDescription,
      });
      form.setError('activityDescription', {
        type: 'custom',
        message:
          failedAssignmentSignature === signature
            ? t('validation.selectActivityBeforeSubmitting')
            : t('validation.assignedActivityMissing'),
      });
      return;
    }

    const factorPayload = factorSelectionPayload;
    const scope2LocationFactor: FactorSelectionInput | undefined =
      scope === 2
        ? {
            ...(manualFactorId.trim() ? { emissionFactorId: manualFactorId.trim() } : {}),
            ...(manualFactorSelectorSource
              ? { emissionFactorSelectorSource: manualFactorSelectorSource }
              : {}),
            factorSelectionMode: manualFactorId.trim() ? 'manual' : 'auto',
          }
        : undefined;
    const scope2MarketFactor: FactorSelectionInput | undefined =
      scope === 2 && values.scope2MarketInputMode === 'market_factor'
        ? {
            ...(scope2MarketManualFactorId.trim()
              ? { emissionFactorId: scope2MarketManualFactorId.trim() }
              : {}),
            ...(scope2MarketManualFactorSelectorSource
              ? { emissionFactorSelectorSource: scope2MarketManualFactorSelectorSource }
              : {}),
            factorSelectionMode: scope2MarketManualFactorId.trim() ? 'manual' : 'auto',
          }
        : undefined;

    const summary = deriveSummaryForMethod({
      activityMethod: values.activityMethod,
      methodValues: normalizedMethodValues,
      methodUnits: normalizedMethodUnits,
    });
    if (
      scope !== 2 &&
      !isCalculationMethodCompatible({
        scope,
        activityCategory: values.activityCategory.trim(),
        calculationMethod: selectedCalculationMethod as ActivityDataCalculationMethod,
        activityMethod: values.activityMethod,
        unit: summary.unit,
      })
    ) {
      form.setError('calculationMethod', {
        type: 'custom',
        message: t('validation.calculationMethodMismatch'),
      });
      return;
    }
    const selectedPeriod = selectedPeriodForForm;
    const scope2Country = values.scope2Country.trim().toUpperCase();
    const scope2AccountingMethod: Scope2AccountingMethod = 'both';
    const normalizedScope2Supplier =
      values.scope2MarketInputMode === 'market_factor'
        ? values.scope2Supplier.trim().toLowerCase().replace(/\s+/g, '_')
        : '';
    const parsedScope2MarketEmissions =
      values.scope2MarketInputMode === 'manual_total' &&
      values.scope2MarketEmissionsKg.trim() &&
      Number.isFinite(Number(values.scope2MarketEmissionsKg.trim()))
        ? Number(values.scope2MarketEmissionsKg.trim())
        : undefined;
    const normalizedDataAccuracyScore = values.dataAccuracyScore.trim()
      ? Number(values.dataAccuracyScore.trim())
      : null;
    const normalizedDataAssumptions = values.dataAssumptions.trim();

    const baseData = {
      scope,
      activityCategory: values.activityCategory.trim(),
      activitySource: selectedActivityChoice?.activitySource ?? 'climatiq',
      activityType: activityTypeForPayload,
      calculationMethod:
        scope === 2 ? undefined : (selectedCalculationMethod as ActivityDataCalculationMethod),
      activityMethod: values.activityMethod,
      activityDescription: values.activityDescription.trim() || undefined,
      methodValues: normalizedMethodValues,
      methodUnits: normalizedMethodUnits,
      methodDisplay: summary.display || undefined,
      reportingPeriodId: values.reportingPeriodId,
      facilityId: values.facilityId !== NO_SELECTION ? values.facilityId : undefined,
      scope3Category: values.scope3Category ? Number(values.scope3Category) : undefined,
      scope2Context:
        scope === 2 && scope2EnergyType
          ? {
              energyType: scope2EnergyType,
              accountingMethod: scope2AccountingMethod,
              country: scope2Country,
              ...(normalizedScope2Supplier ? { supplier: normalizedScope2Supplier } : {}),
              ...(values.scope2MarketInputMode === 'market_factor' &&
              values.scope2LegacyMarketFactorKgCo2ePerKwh.trim() &&
              Number.isFinite(Number(values.scope2LegacyMarketFactorKgCo2ePerKwh.trim()))
                ? {
                    marketFactorKgCo2ePerKwh: Number(
                      values.scope2LegacyMarketFactorKgCo2ePerKwh.trim()
                    ),
                  }
                : {}),
              ...(selectedPeriod?.startDate
                ? { year: new Date(selectedPeriod.startDate).getFullYear() }
                : {}),
            }
          : undefined,
      ...(scope === 2 ? { scope2LocationFactor } : {}),
      ...(scope === 2 ? { scope2MarketFactor } : {}),
      ...(scope === 2 ? { scope2MarketEmissionsKg: parsedScope2MarketEmissions } : {}),
      dataAccuracyScore:
        mode === 'edit' ? normalizedDataAccuracyScore : (normalizedDataAccuracyScore ?? undefined),
      dataAssumptions:
        mode === 'edit'
          ? normalizedDataAssumptions || null
          : normalizedDataAssumptions || undefined,
      dataIsEstimated: values.dataIsEstimated,
      notes: values.notes || undefined,
      emissionFactorId: scope === 2 ? undefined : factorPayload.emissionFactorId,
      emissionFactorSelectorSource:
        scope === 2 ? undefined : factorPayload.emissionFactorSelectorSource,
      factorSelectionMode: scope === 2 ? undefined : factorPayload.factorSelectionMode,
      emissionFactorParameters: scope === 2 ? undefined : factorPayload.emissionFactorParameters,
    };

    if (mode === 'create') {
      const result = await createMutation.mutateAsync({
        ...baseData,
        taskId: taskId ?? undefined,
      } as CreateActivityDataInput);
      onSuccess(result);
      return;
    }

    if (existingEntry) {
      const updateData =
        scope === 2
          ? {
              ...baseData,
              scope2MarketFactor:
                values.scope2MarketInputMode === 'market_factor' ? scope2MarketFactor : null,
              scope2MarketEmissionsKg:
                values.scope2MarketInputMode === 'manual_total'
                  ? (parsedScope2MarketEmissions ?? null)
                  : null,
            }
          : baseData;
      const result = await updateMutation.mutateAsync({
        entryId: existingEntry.id,
        data: updateData,
      });
      onSuccess(result);
    }
  };

  if (mode === 'create' && createStep === 1) {
    return (
      /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */
      <ActivityFormStepOne
        form={form as any}
        periodOptions={periodOptions}
        hasPeriods={hasPeriods && !periodsLoading}
        isLoading={isLoading}
        scopeSelection={createScopeSelection}
        categorySelection={createCategorySelection}
        fixedScope={Boolean(effectiveFixedScope)}
        fixedCategory={Boolean(fixedScope3CategorySelection)}
        error={createStepError}
        categoryHelperText={selectedCreateCategory?.helperText}
        onScopeChange={(value) => {
          if (effectiveFixedScope) return;
          setCreateScopeSelection(value);
          setCreateCategorySelection('');
          setCreateStepError('');
          form.clearErrors();
        }}
        onCategoryChange={(value) => {
          if (fixedScope3CategorySelection) return;
          setCreateCategorySelection(value);
          setCreateStepError('');
          form.clearErrors();
        }}
        onContinue={handleContinueToStep2}
        onCancel={handleCancel}
        onPeriodChange={setPeriodId}
      />
      /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment */
    );
  }

  return (
    <Form
      form={form}
      onSubmit={(values) => void handleSubmit(values)}
      onSubmitInvalid={() => {
        const errorKeys = Object.keys(form.formState.errors);
        if (errorKeys.length > 0) {
          toast.error(t('ghg:toast.pleaseFixTheHighlightedFieldsBeforeSubmitting'), {
            description: t('activityDataEntryForm.toast_fields_require_attention', { count: errorKeys.length }),
          });
          // Scroll to first error
          const firstErrorEl = document.querySelector('[data-state="error"], .text-destructive');
          if (firstErrorEl) {
            firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }}
      className="space-y-6"
    >
      {isApproved && (
        <Alert>
          <AlertDescription>
            {isEntryApproved
              ? t('activityDataEntryForm.alert_entry_approved')
              : t('activityDataEntryForm.alert_read_only')}
          </AlertDescription>
        </Alert>
      )}

      {!periodsLoading && !hasPeriods && mode === 'create' && (
        <Alert variant="destructive">
          <AlertDescription>
            <strong>{t('activityDataEntryForm.alert_reporting_period_required_title')}</strong>{' '}
            {t('activityDataEntryForm.alert_reporting_period_required_desc')}
          </AlertDescription>
        </Alert>
      )}

      {mode === 'create' && (
        <ActivityFormHeader
          scope={watchedScope}
          activityCategory={watchedActivityCategory}
          categoryLabel={selectedCreateCategory?.label ?? resolvedCreateCategory}
          isCreateMode
          isLoading={isLoading}
          onBack={() => {
            setCreateStep(1);
          }}
        />
      )}

      {mode === 'edit' && (
        <ReportingPeriodField
          control={form.control}
          options={periodOptions}
          disabled={isApproved}
          description={t('activityDataEntryForm.desc_select_the_period_this_data_applies_to')}
        />
      )}

      {mode === 'edit' && (
        <FormField
          control={form.control}
          name="scope"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('activityDataEntryForm.label_scope')}</FormLabel>
              <FormControl>
                <Select
                  options={activityEntryScopeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  disabled
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FacilityField
        control={form.control}
        options={facilityOptions}
        disabled={isApproved}
        label={t('activityDataEntryForm.label_facility_optional')}
        description={t('activityDataEntryForm.desc_select_facility')}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        {mode === 'edit' && (
          <FormField
            control={form.control}
            name="activityCategory"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('activityDataEntryForm.label_activity_category')}</FormLabel>
                <FormControl>
                  <Select
                    options={editCategoryOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setDiscoveredActivity(null);
                      setManualFactorId('');
                      form.clearErrors('emissionFactorId');
                    }}
                    placeholder={t('placeholder.selectACategory1')}
                    disabled
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {!showScope2Context && (
          <FormField
            control={form.control}
            name="calculationMethod"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('activityDataEntryForm.label_calculation_method')}</FormLabel>
                <FormControl>
                  <Select
                    options={calculationMethodOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      lastAutoCalculationMethodRef.current = '';
                      form.clearErrors('calculationMethod');
                    }}
                    placeholder={t('placeholder.selectCalculationMethod')}
                    disabled={isApproved}
                  />
                </FormControl>
                <FormDescription>
                  {t('activityDataEntryForm.desc_calculation_method')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="activityMethod"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('activityDataEntryForm.label_unit_type')}</FormLabel>
              <FormControl>
                <Select
                  options={methodOptions}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    form.setValue('activityType', '', {
                      shouldDirty: watchedScope !== 2,
                      shouldValidate: false,
                    });
                    setDiscoveredActivity(null);
                    setAiAssignedChoice(null);
                    setConfirmedAssignmentSignature('');
                    setConfirmedAssignmentActivityId('');
                    setFailedAssignmentSignature('');
                    setManualFactorId('');
                    form.clearErrors('activityMethod');
                    form.clearErrors('activityType');
                    form.clearErrors('emissionFactorId');
                  }}
                  placeholder={t('placeholder.selectUnitType')}
                  disabled={isApproved || mode === 'edit'}
                />
              </FormControl>
              <FormDescription>{t('activityDataEntryForm.desc_unit_type')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {(watchedScope === 1 || watchedScope === 2 || showScope3AutopilotAssist) &&
        !showScope3WasteCodeFlow && (
          <div className="space-y-3">
            {factorSourceChoice !== 'predefined' && factorSegment !== 'predefined' && (
              <FormField
                control={form.control}
                name="activityDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('activityDataEntryForm.label_activity_description')}</FormLabel>
                    <FormControl>
                      <ActivityDescriptionAutocomplete
                        value={field.value}
                        onChange={(val) => {
                          field.onChange(val);
                          if (val.trim().length > 0) {
                            form.clearErrors('activityDescription');
                          }
                        }}
                        onSelectSuggestion={(suggestion) => {
                          field.onChange(suggestion.description);
                          form.clearErrors('activityDescription');

                          // Auto-update activityMethod if the suggestion has unit type info
                          if (suggestion.unitType && suggestion.unitType !== watchedActivityMethod) {
                            form.setValue('activityMethod', suggestion.unitType, { shouldDirty: true, shouldValidate: true });
                          }

                          const activityId = suggestion.factorId || suggestion.id;
                          const existingChoice = activityChoices.find(
                            (c) => c.activityId.trim() === activityId.trim()
                          );
                          if (existingChoice) {
                            form.setValue('activityType', existingChoice.value, { shouldDirty: true, shouldValidate: true });
                          } else {
                            const injectedChoice: ActivityChoice = {
                              value: activityId,
                              label: suggestion.activityName,
                              activityId,
                              activitySource: 'climatiq',
                              unitType: suggestion.unitType || watchedActivityMethod,
                              source: 'mapping_memory',
                              notes: `From autocomplete: ${suggestion.description}`,
                            };
                            setAiAssignedChoice(injectedChoice);
                            form.setValue('activityType', injectedChoice.value, { shouldDirty: true, shouldValidate: true });
                          }
                          setManualFactorId('');
                          setFactorSegment('last-used');
                          toast.success(`Mapping applied: ${suggestion.activityName}`);
                        }}
                        unitType={watchedActivityMethod || undefined}
                        placeholder={t('activityDataEntryForm.placeholder_activity_description')}
                        disabled={isApproved}
                      />
                    </FormControl>
                    <FormDescription>{activityDescriptionHelpText}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FactorSegmentedSelector
              activeSegment={factorSegment}
              onSegmentChange={setFactorSegment}
              factorSourceChoice={factorSourceChoice}
              scope={watchedScope || undefined}
              activityCategory={watchedActivityCategory || undefined}
              unitType={watchedActivityMethod || undefined}
              lastUsedFactors={memorySuggestions.filter(
                (s) => !watchedScope || s.scope === `Scope ${String(watchedScope)}`
              )}
              onSelectLastUsed={(item) => {
                form.setValue('activityDescription', item.description);
                form.clearErrors('activityDescription');

                // Auto-update activityMethod if the item has unit type info
                if (item.unitType && item.unitType !== watchedActivityMethod) {
                  form.setValue('activityMethod', item.unitType, { shouldDirty: true, shouldValidate: true });
                }

                // Find existing catalog choice matching this activity ID
                const activityId = item.factorId || item.id;
                const existingChoice = activityChoices.find(
                  (c) => c.activityId.trim() === activityId.trim()
                );
                if (existingChoice) {
                  form.setValue('activityType', existingChoice.value, { shouldDirty: true, shouldValidate: true });
                } else {
                  // No matching catalog entry — inject one
                  const injectedChoice: ActivityChoice = {
                    value: activityId,
                    label: item.activityName,
                    activityId,
                    activitySource: 'climatiq',
                    unitType: item.unitType || watchedActivityMethod,
                    source: 'mapping_memory',
                    notes: `From mapping: ${item.description}`,
                  };
                  setAiAssignedChoice(injectedChoice);
                  form.setValue('activityType', injectedChoice.value, { shouldDirty: true, shouldValidate: true });
                }
                setManualFactorId('');
                toast.success(`Mapping applied: ${item.activityName}`);
              }}
              onSelectPredefined={(factor) => {
                if (watchedScope && factor.scope !== watchedScope) {
                  toast.warning(
                    `This factor is for Scope ${String(factor.scope)} but the form is set to Scope ${String(watchedScope)}.`
                  );
                  return;
                }

                form.setValue('activityDescription', factor.name);
                form.clearErrors('activityDescription');

                if (!factor.climatiqActivityId) {
                  toast.warning(`${factor.name} has no Climatiq mapping — cannot apply.`);
                  return;
                }

                // Auto-update activityMethod if the factor's unit type differs
                const factorUnitType = factor.unitType || (factor.unit ? inferUnitTypeFromDefaultUnit(factor.unit) : undefined);
                if (factorUnitType && factorUnitType !== watchedActivityMethod) {
                  form.setValue('activityMethod', factorUnitType, { shouldDirty: true, shouldValidate: true });
                }

                {
                  // Route through the standard Climatiq resolve pipeline.
                  // activitySource 'climatiq' lets the resolver fetch a proper
                  // factor UUID; source 'predefined' is kept for tracking only.
                  const climatiqId = factor.climatiqActivityId;
                  const existingChoice = activityChoices.find(
                    (c) => c.activityId.trim() === climatiqId.trim()
                  );
                  if (existingChoice) {
                    form.setValue('activityType', existingChoice.value, { shouldDirty: true, shouldValidate: true });
                  } else {
                    const injectedChoice: ActivityChoice = {
                      value: `predefined:${climatiqId}`,
                      label: factor.name,
                      activityId: climatiqId,
                      activitySource: 'climatiq',
                      unitType: factorUnitType || watchedActivityMethod,
                      source: 'predefined',
                      notes: `Predefined: ${factor.source} ${String(factor.year)}`,
                    };
                    setAiAssignedChoice(injectedChoice);
                    form.setValue('activityType', injectedChoice.value, { shouldDirty: true, shouldValidate: true });
                  }
                  // Clear manual factor — let auto-resolve find the proper
                  // Climatiq factor UUID from the activity_id.
                  setManualFactorId('');
                  setManualFactorPreview(undefined);
                  setManualFactorSelectorSource(undefined);
                  toast.success(`Predefined factor applied: ${factor.name} (${factor.co2ePerUnit} ${factor.co2eUnit})`);
                }
              }}
              aiAssignmentContent={
                implicitScope2ActivityType ? (
                  <p className="text-xs text-muted-foreground">{scope2ImplicitActivityMessage}</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (watchedScope === 3 && scope3AutopilotCategory) {
                          void handleAutoDetectScope3Activity();
                        } else {
                          void handleAutoAssignActivity();
                        }
                      }}
                      disabled={
                        isApproved ||
                        (watchedScope === 3
                          ? !canAutoDetectScope3Activity || suggestAutopilotMutation.isPending
                          : !canAutoAssignActivity || suggestAssignmentMutation.isPending || confirmAssignmentMutation.isPending)
                      }
                    >
                      {(suggestAssignmentMutation.isPending || suggestAutopilotMutation.isPending || confirmAssignmentMutation.isPending) && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Auto-detect activity
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Uses AI-powered matching. Requires explicit confirmation.
                    </p>
                    {(llmUnavailable || failedAssignmentSignature === currentAssignmentSignature) && watchedScope !== 3 && (
                      <p className="text-xs text-amber-700">
                        Auto-detect is unavailable right now. You can submit with a manually selected
                        activity.
                      </p>
                    )}
                    {failedAutopilotSignature === currentAutopilotSignature && watchedScope === 3 && (
                      <p className="text-xs text-amber-700">
                        Auto-detect is unavailable right now. You can continue with manual activity
                        search.
                      </p>
                    )}
                    {emptyAutopilotSignature === currentAutopilotSignature && watchedScope === 3 && (
                      <p className="text-xs text-amber-700">
                        No suggestion matched this description. Continue with manual activity search.
                      </p>
                    )}
                  </div>
                )
              }
              manualSearchContent={
                <div className="space-y-3">
                  <Scope3ActivityPicker
                    scope={watchedScope as 1 | 2 | 3}
                    activityCategory={watchedActivityCategory}
                    {...(watchedScope3Category.trim()
                      ? { scope3Category: Number(watchedScope3Category) }
                      : {})}
                    selectedMethod={watchedActivityMethod}
                    disabled={
                      isApproved ||
                      !watchedActivityMethod ||
                      (watchedScope === 3 && !watchedScope3Category.trim())
                    }
                    onUseActivity={handleUseDiscoveredActivity}
                  />
                </div>
              }
            />
          </div>
        )}

      {showScope3WasteCodeFlow && (
        <div className="space-y-4 rounded-lg border bg-muted/10 p-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('activityDataEntryForm.label_ewc_heading')}</p>
            <p className="text-xs text-muted-foreground">
              {t('activityDataEntryForm.desc_ewc_deterministic')}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <FormLabel>{t('activityDataEntryForm.label_ewc_code')}</FormLabel>
              <Input
                value={wasteCodeInput}
                onChange={(event) => {
                  setWasteCodeInput(event.target.value);
                }}
                placeholder={t('activityDataEntryForm.placeholder_ewc_code')}
                disabled={isApproved}
              />
              <p className="text-xs text-muted-foreground">
                {t('activityDataEntryForm.desc_ewc_formats')}
              </p>
            </div>

            <div className="space-y-1">
              <FormLabel>{t('activityDataEntryForm.label_waste_treatment')}</FormLabel>
              <Select
                options={[
                  { value: '', label: 'Select option' },
                  ...(matchedWasteCodeEntry?.treatmentOptions ?? []).map((option) => ({
                    value: option.treatmentId,
                    label: option.label,
                  })),
                ]}
                value={selectedWasteCodeTreatmentId}
                onChange={(value) => {
                  setSelectedWasteCodeTreatmentId(value);
                }}
                placeholder={t('activityDataEntryForm.placeholder_select_treatment')}
                disabled={isApproved}
              />
              <p className="text-xs text-muted-foreground">
                {matchedWasteCodeEntry
                  ? t('activityDataEntryForm.desc_treatment_loaded', { source: matchedWasteCodeEntry.source, family: matchedWasteCodeEntry.familyLabel })
                  : t('activityDataEntryForm.desc_treatment_pending')}
              </p>
            </div>
          </div>

          <div className="rounded-md border bg-background p-3 text-xs">
            {wasteCodeResolutionQuery.isLoading || wasteCodeResolutionQuery.isFetching ? (
              <p className="text-muted-foreground">
                {t('activityDataEntryForm.label_resolving_waste')}
              </p>
            ) : matchedWasteCodeEntry ? (
              <div className="space-y-1 text-muted-foreground">
                <p>{wasteCodeResolutionMessage}</p>
                <p>{t('activityDataEntryForm.desc_code_mapping')}</p>
                <p>{t('activityDataEntryForm.label_source_label')} {matchedWasteCodeEntry.sourceLabel}</p>
                <p>{t('activityDataEntryForm.label_manifest_version')} {wasteCodeResolutionQuery.data?.manifestVersion ?? 'N/A'}</p>
              </div>
            ) : wasteCodeResolutionQuery.data?.status ? (
              <div className="space-y-1">
                {wasteCodeResolutionMessage ? (
                  <p className="text-amber-700">{wasteCodeResolutionMessage}</p>
                ) : null}
                {wasteCodeResolutionQuery.data.ambiguityOptions.length > 0 ? (
                  <div className="space-y-1 text-muted-foreground">
                    {wasteCodeResolutionQuery.data.ambiguityOptions.map((option) => (
                      <p key={option.canonicalCode}>
                        {option.canonicalCode} · {option.familyLabel} ({option.source})
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    {t('activityDataEntryForm.desc_use_manual_search')}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">
                {t('activityDataEntryForm.desc_enter_ewc')}
              </p>
            )}
          </div>
        </div>
      )}

      {showScope2Context && (
        <div className="space-y-4 rounded-lg border bg-muted/10 p-4">
          <FormField
            control={form.control}
            name="scope2Country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('activityDataEntryForm.label_country')}</FormLabel>
                <FormControl>
                  <Select
                    options={
                      allGridRegionsLoading
                        ? [{ value: '', label: 'Loading countries...' }]
                        : scope2CountryOptions.length > 0
                          ? scope2CountryOptions
                          : [{ value: '', label: 'No countries available' }]
                    }
                    value={field.value}
                    onChange={(nextValue) => {
                      form.setValue('scope2Country', nextValue.trim().toUpperCase(), {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      form.clearErrors('scope2Country');
                    }}
                    placeholder={t('placeholder.selectCountry')}
                    disabled={isApproved}
                  />
                </FormControl>
                <FormDescription>
                  {t('activityDataEntryForm.desc_country_required')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {watchedScope2Country && (
            <p className="text-xs text-muted-foreground">
              {t('activityDataEntryForm.desc_scope2_both')}
            </p>
          )}

          {implicitScope2ActivityType && (
            <p className="text-xs text-muted-foreground">{scope2ImplicitActivityContextNote}</p>
          )}

          <div className="space-y-3 rounded-md border border-dashed p-3">
            <p className="text-sm font-medium">{t('activityDataEntryForm.label_location_based_factor')}</p>
            {scope2PreviewQuery.data?.scope2LocationFactor ? (
              renderEmissionFactorCard({
                name:
                  scope2PreviewQuery.data.scope2LocationFactor.emissionFactorName ??
                  'Location-based factor',
                source: scope2PreviewQuery.data.scope2LocationFactor.emissionFactorSource,
                regionName: scope2PreviewQuery.data.scope2RegionCode ?? watchedScope2Country,
                year:
                  (scope2PreviewQuery.data.scope2EstimateMeta as { year?: number } | null)?.year ??
                  null,
                unit:
                  scope2PreviewQuery.data.scope2LocationFactor.emissionFactorRawUnit ??
                  scope2PreviewQuery.data.scope2LocationFactor.emissionFactorUnitType,
                reason: manualFactorId
                  ? 'Manual override applied to the location-based factor.'
                  : 'Auto-resolved from Scope 2 context.',
                factorId:
                  scope2PreviewQuery.data.scope2LocationFactor.emissionFactorId ?? undefined,
                unitType:
                  scope2PreviewQuery.data.scope2LocationFactor.emissionFactorUnitType ?? undefined,
                dataVersion:
                  scope2PreviewQuery.data.scope2LocationFactor.emissionFactorDataVersion ??
                  undefined,
              })
            ) : (
              <p className="text-xs text-muted-foreground">
                {t('activityDataEntryForm.desc_complete_quantity')}
              </p>
            )}

            <div className="flex gap-2">
              {manualFactorId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setManualFactorId('');
                    setManualFactorSelectorSource(undefined);
                    setManualFactorPreview(undefined);
                  }}
                  disabled={isApproved}
                >
                  {t('activityDataEntryForm.btn_use_auto_resolved_factor')}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAdvancedFactorPicker((previous) => !previous);
                }}
                disabled={isApproved}
              >
                {showAdvancedFactorPicker ? t('activityDataEntryForm.btn_hide_factor_search') : t('activityDataEntryForm.btn_change_location_factor')}
              </Button>
            </div>

            {showAdvancedFactorPicker && (
              <div className="rounded-md border bg-background p-3">
                <EmissionFactorSearch
                  context={manualFactorSearchContext}
                  {...(manualFactorInitialParams
                    ? { initialParams: manualFactorInitialParams }
                    : {})}
                  onSelectFactor={(factorId, factor) => {
                    setManualFactorId(factorId);
                    setManualFactorSelectorSource(factor?.selectorSource ?? 'climatiq');
                    setManualFactorPreview(factor);
                    setShowAdvancedFactorPicker(false);
                  }}
                />
              </div>
            )}
          </div>

          <FormField
            control={form.control}
            name="scope2MarketInputMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('activityDataEntryForm.label_market_based_input')}</FormLabel>
                <FormControl>
                  <Select
                    options={[
                      { value: 'same_as_location', label: t('activityDataEntryForm.option_same_as_location') },
                      { value: 'market_factor', label: t('activityDataEntryForm.option_market_factor') },
                      { value: 'manual_total', label: t('activityDataEntryForm.option_manual_total') },
                    ]}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      if (value !== 'market_factor') {
                        setScope2MarketManualFactorId('');
                        setScope2MarketManualFactorSelectorSource(undefined);
                        setShowScope2MarketFactorPicker(false);
                        form.setValue('scope2Supplier', '', { shouldDirty: true });
                        form.setValue('scope2LegacyMarketFactorKgCo2ePerKwh', '', {
                          shouldDirty: true,
                        });
                      }
                      if (value !== 'manual_total') {
                        form.setValue('scope2MarketEmissionsKg', '', { shouldDirty: true });
                      }
                    }}
                    placeholder={t('placeholder.selectMarketbasedInputMode')}
                    disabled={isApproved}
                  />
                </FormControl>
                <FormDescription>
                  {t('activityDataEntryForm.desc_market_default')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {watchedScope2MarketInputMode === 'market_factor' && (
            <div className="space-y-3 rounded-md border border-dashed p-3">
              <p className="text-sm font-medium">{t('activityDataEntryForm.label_market_based_factor')}</p>
              {showScope2SupplierField && (
                <FormField
                  control={form.control}
                  name="scope2Supplier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('activityDataEntryForm.label_supplier_optional')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('activityDataEntryForm.placeholder_e_g_british_gas')}
                          disabled={isApproved}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('activityDataEntryForm.desc_supplier_token')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {watchedScope2LegacyMarketFactorKgCo2ePerKwh.trim() &&
                !scope2MarketManualFactorId.trim() && (
                  <Alert>
                    <AlertDescription>
                      {t('activityDataEntryForm.alert_legacy_market_factor')}
                    </AlertDescription>
                  </Alert>
                )}

              {scope2PreviewQuery.data?.scope2MarketFactor ? (
                renderEmissionFactorCard({
                  name:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorName ??
                    'Market-based factor',
                  source: scope2PreviewQuery.data.scope2MarketFactor.emissionFactorSource,
                  regionName: scope2PreviewQuery.data.scope2RegionCode ?? watchedScope2Country,
                  year:
                    (scope2PreviewQuery.data.scope2EstimateMeta as { year?: number } | null)
                      ?.year ?? null,
                  unit:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorRawUnit ??
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorUnitType,
                  reason: scope2MarketManualFactorId
                    ? 'Manual override applied to the market-based factor.'
                    : 'Auto-resolved from market-based Scope 2 context.',
                  factorId:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorId ?? undefined,
                  unitType:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorUnitType ?? undefined,
                  dataVersion:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorDataVersion ??
                    undefined,
                })
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('activityDataEntryForm.desc_market_auto_resolved')}
                </p>
              )}

              <div className="flex gap-2">
                {scope2MarketManualFactorId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setScope2MarketManualFactorId('');
                      setScope2MarketManualFactorSelectorSource(undefined);
                    }}
                    disabled={isApproved}
                  >
                    {t('activityDataEntryForm.btn_use_auto_resolved_market')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowScope2MarketFactorPicker((previous) => !previous);
                  }}
                  disabled={isApproved}
                >
                  {showScope2MarketFactorPicker ? t('activityDataEntryForm.btn_hide_factor_search') : t('activityDataEntryForm.btn_change_market_factor')}
                </Button>
              </div>

              {showScope2MarketFactorPicker && (
                <div className="rounded-md border bg-background p-3">
                  <EmissionFactorSearch
                    context={manualFactorSearchContext}
                    {...(manualFactorInitialParams
                      ? { initialParams: manualFactorInitialParams }
                      : {})}
                    onSelectFactor={(factorId, factor) => {
                      setScope2MarketManualFactorId(factorId);
                      setScope2MarketManualFactorSelectorSource(
                        factor?.selectorSource ?? 'climatiq'
                      );
                      setShowScope2MarketFactorPicker(false);
                      form.setValue('scope2LegacyMarketFactorKgCo2ePerKwh', '', {
                        shouldDirty: true,
                      });
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {watchedScope2MarketInputMode === 'manual_total' && (
            <FormField
              control={form.control}
              name="scope2MarketEmissionsKg"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('activityDataEntryForm.label_market_emissions_total')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      placeholder={t('activityDataEntryForm.placeholder_e_g_3200')}
                      disabled={isApproved}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('activityDataEntryForm.desc_market_emissions_total')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {form.formState.errors.emissionFactorId?.message && (
            <p className="text-sm text-destructive">
              {form.formState.errors.emissionFactorId.message}
            </p>
          )}
        </div>
      )}

      {!implicitScope2ActivityType && (
        <FormField
          control={form.control}
          name="activityType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{showScope3WasteCodeFlow ? t('label_resolved_activity') : t('activityDataEntryForm.label_activity')}</FormLabel>
              <div className="space-y-3">
                <FormControl>
                  <Input type="hidden" {...field} />
                </FormControl>
                <div className="rounded-md border bg-background p-3">
                  {selectedActivityChoice ? (
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{selectedActivityChoice.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('activityDataEntryForm.label_unit_type_info')}: {selectedActivityChoice.unitType}
                        {selectedActivityChoice.defaultUnit
                          ? ` · ${t('activityDataEntryForm.label_default_unit')}: ${selectedActivityChoice.defaultUnit}`
                          : ''}
                      </p>
                      {selectedActivityChoice.activityId && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                            {t('activityDataEntryForm.label_show_technical')}
                          </summary>
                          <p className="mt-1 font-mono text-xs text-muted-foreground">
                            {selectedActivityChoice.activityId}
                          </p>
                        </details>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {showScope3WasteCodeFlow
                        ? t('activityDataEntryForm.no_activity_waste_code')
                        : showScope3AutopilotAssist
                          ? t('activityDataEntryForm.no_activity_autopilot')
                          : t('activityDataEntryForm.no_activity_manual')}
                    </p>
                  )}
                </div>
                {/* Activity search is now inside the Manual Search segment of FactorSegmentedSelector */}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {!showScope2Context && activityListEmptyForMethod && !showScope3WasteCodeFlow && (
        <Alert>
          <AlertDescription>
            {showScope3AutopilotAssist
              ? t('activityDataEntryForm.no_activities_autopilot')
              : t('activityDataEntryForm.no_activities_manual')}
          </AlertDescription>
        </Alert>
      )}

      <ActivityFormQuantityInputs
        activityMethod={watchedActivityMethod}
        methodValues={methodValues}
        methodUnits={methodUnits}
        onValueChange={(key, value) => {
          setMethodValues((prev) => ({ ...prev, [key]: value }));
        }}
        onUnitChange={(key, value) => {
          setMethodUnits((prev) => ({ ...prev, [key]: value }));
        }}
        disabled={isApproved}
        error={methodFormError}
      />

      {watchedScope !== 2 ? (
        <div className="space-y-3 rounded-lg border bg-muted/10 p-4">
          <p className="text-sm font-medium">{t('activityDataEntryForm.label_emission_factor')}</p>

          {showScope3WasteCodeFlow && (
            <p className="text-xs text-muted-foreground">
              The recommended factor is resolved from the EWC / BDO code mapping and selected
              treatment. Use manual factor search only if you need to override it.
            </p>
          )}

          <ActivityFormFactorBadge
            factor={selectedFactorCandidate}
            mode={selectedMode}
            reason={factorReason}
            loading={isFactorResolving}
            error={factorBadgeError}
            disabled={isApproved}
            onOverride={() => {
              setShowAdvancedFactorPicker(true);
            }}
          />

          {form.formState.errors.emissionFactorId?.message && (
            <p className="text-sm text-destructive">
              {form.formState.errors.emissionFactorId.message}
            </p>
          )}

          <ActivityFormEstimateInline
            estimate={
              activityPreviewQuery.data
                ? { totalCo2eKg: activityPreviewQuery.data.estimatedCo2eKg }
                : null
            }
            loading={activityPreviewQuery.isLoading || activityPreviewQuery.isFetching}
            blockedReason={activityPreviewBlockedReason || null}
          />

          <div className="space-y-2 rounded-md border border-dashed p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">{t('activityDataEntryForm.label_advanced')}</p>
              <div className="flex gap-2">
                {manualFactorId && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setManualFactorId('');
                      form.clearErrors('emissionFactorId');
                    }}
                    disabled={isApproved}
                  >
                    {t('activityDataEntryForm.btn_use_recommended_factor')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAdvancedFactorPicker((previous) => !previous);
                  }}
                  disabled={isApproved}
                >
                  {showAdvancedFactorPicker ? t('activityDataEntryForm.btn_hide_factor_search') : t('activityDataEntryForm.btn_change_emission_factor')}
                </Button>
              </div>
            </div>

            {showAdvancedFactorPicker && (
              <div className="rounded-md border bg-background p-3">
                <EmissionFactorSearch
                  context={manualFactorSearchContext}
                  {...(manualFactorInitialParams
                    ? { initialParams: manualFactorInitialParams }
                    : {})}
                  onSelectFactor={(factorId, factor) => {
                    setManualFactorId(factorId);
                    setManualFactorSelectorSource(factor?.selectorSource ?? 'climatiq');
                    setManualFactorPreview(factor);
                    setShowAdvancedFactorPicker(false);
                    form.clearErrors('emissionFactorId');
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3 rounded-lg border bg-muted/10 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">{t('activityDataEntryForm.label_scope2_preview')}</p>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${factorSelectionModeClasses(scope2PreviewQuery.data?.scope2LocationFactor?.factorSelectionMode ?? (manualFactorId ? 'manual' : 'auto'))}`}
            >
              {factorSelectionModeLabel(
                scope2PreviewQuery.data?.scope2LocationFactor?.factorSelectionMode ??
                  (manualFactorId ? 'manual' : 'auto'),
                t
              )}
            </span>
          </div>

          {scope2PreviewBlockedReason ? (
            <Alert>
              <AlertDescription>{scope2PreviewBlockedReason}</AlertDescription>
            </Alert>
          ) : scope2PreviewQuery.isLoading || scope2PreviewQuery.isFetching ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('activityDataEntryForm.label_previewing_scope2')}
            </div>
          ) : scope2PreviewQuery.error ? (
            <Alert variant="destructive">
              <AlertDescription>{scope2PreviewQuery.error.message}</AlertDescription>
            </Alert>
          ) : scope2PreviewQuery.data ? (
            <div className="space-y-3 rounded-md border bg-background p-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">{t('activityDataEntryForm.label_primary_estimate')}</p>
                  <p className="text-sm font-medium">
                    {formatKgCo2e(scope2PreviewQuery.data.estimatedCo2eKg, t)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Primary method:{' '}
                    {formatScope2MethodLabel(scope2PreviewQuery.data.scope2PrimaryMethod, t)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('activityDataEntryForm.label_input_quantity')}</p>
                  <p className="text-sm font-medium">{scope2PreviewSummary?.display ?? 'N/A'}</p>
                  <p className="text-xs text-muted-foreground">
                    Accounting:{' '}
                    {formatScope2MethodLabel(scope2PreviewQuery.data.scope2AccountingMethod, t)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('activityDataEntryForm.label_location_based')}</p>
                  <p className="text-sm font-medium">
                    {formatKgCo2e(scope2PreviewQuery.data.scope2LocationCo2eKg, t)}
                  </p>
                  {scope2PreviewSummary && scope2PreviewSummary.quantity > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {scope2PreviewQuery.data.scope2LocationCo2eKg != null
                        ? `${(
                            scope2PreviewQuery.data.scope2LocationCo2eKg /
                            scope2PreviewSummary.quantity
                          ).toLocaleString(undefined, {
                            maximumFractionDigits: 8,
                          })} kg CO2e / ${scope2PreviewSummary.unit || 'unit'}`
                        : 'Intensity unavailable'}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('activityDataEntryForm.label_market_based')}</p>
                  <p className="text-sm font-medium">
                    {formatKgCo2e(scope2PreviewQuery.data.scope2MarketCo2eKg, t)}
                  </p>
                  {scope2PreviewSummary && scope2PreviewSummary.quantity > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {scope2PreviewQuery.data.scope2MarketCo2eKg != null
                        ? `${(
                            scope2PreviewQuery.data.scope2MarketCo2eKg /
                            scope2PreviewSummary.quantity
                          ).toLocaleString(undefined, {
                            maximumFractionDigits: 8,
                          })} kg CO2e / ${scope2PreviewSummary.unit || 'unit'}`
                        : 'Intensity unavailable'}
                    </p>
                  )}
                </div>
              </div>

              {scope2PreviewQuery.data.scope2LocationFactor &&
                renderEmissionFactorCard({
                  name:
                    scope2PreviewQuery.data.scope2LocationFactor.emissionFactorName ??
                    'Location-based factor',
                  source: scope2PreviewQuery.data.scope2LocationFactor.emissionFactorSource,
                  regionName:
                    scope2PreviewQuery.data.scope2RegionCode ??
                    scope2PreviewQuery.data.scope2Country ??
                    'Scope 2 context',
                  year:
                    (scope2PreviewQuery.data.scope2EstimateMeta as { year?: number } | null)
                      ?.year ?? null,
                  unit:
                    scope2PreviewQuery.data.scope2LocationFactor.emissionFactorRawUnit ??
                    scope2PreviewQuery.data.scope2LocationFactor.emissionFactorUnitType,
                  reason: manualFactorId
                    ? 'Manual override applied to the location-based factor.'
                    : 'Location-based factor resolved from Scope 2 context.',
                  factorId:
                    scope2PreviewQuery.data.scope2LocationFactor.emissionFactorId ?? undefined,
                  unitType:
                    scope2PreviewQuery.data.scope2LocationFactor.emissionFactorUnitType ??
                    undefined,
                  dataVersion:
                    scope2PreviewQuery.data.scope2LocationFactor.emissionFactorDataVersion ??
                    undefined,
                })}

              {scope2PreviewQuery.data.scope2MarketFactor &&
                renderEmissionFactorCard({
                  name:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorName ??
                    'Market-based factor',
                  source: scope2PreviewQuery.data.scope2MarketFactor.emissionFactorSource,
                  regionName:
                    scope2PreviewQuery.data.scope2RegionCode ??
                    scope2PreviewQuery.data.scope2Country ??
                    'Scope 2 context',
                  year:
                    (scope2PreviewQuery.data.scope2EstimateMeta as { year?: number } | null)
                      ?.year ?? null,
                  unit:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorRawUnit ??
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorUnitType,
                  reason:
                    watchedScope2MarketInputMode === 'market_factor'
                      ? scope2MarketManualFactorId
                        ? 'Manual override applied to the market-based factor.'
                        : 'Market-based factor resolved from market-specific Scope 2 context.'
                      : 'Market-specific factor applied.',
                  factorId:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorId ?? undefined,
                  unitType:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorUnitType ?? undefined,
                  dataVersion:
                    scope2PreviewQuery.data.scope2MarketFactor.emissionFactorDataVersion ??
                    undefined,
                })}

              {watchedScope2MarketInputMode === 'same_as_location' && (
                <p className="text-xs text-muted-foreground">
                  Market-based emissions currently mirror the location-based factor.
                </p>
              )}

              {watchedScope2MarketInputMode === 'manual_total' && (
                <p className="text-xs text-muted-foreground">
                  Market-based emissions are using the manual total entered above.
                </p>
              )}
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                {t('activityDataEntryForm.alert_preview_unavailable')}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {showScope3Category &&
        (mode === 'create' && defaultScope3Category ? (
          <FormItem>
            <FormLabel>{t('activityDataEntryForm.label_scope3_category')}</FormLabel>
            <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
              {String(defaultScope3Category)} -{' '}
              {SCOPE3_CATEGORIES_META.find((category) => category.number === defaultScope3Category)
                ?.name ?? ''}
            </div>
            <FormDescription>
              {t('activityDataEntryForm.desc_scope3_derived')}
            </FormDescription>
          </FormItem>
        ) : (
          <FormField
            control={form.control}
            name="scope3Category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('activityDataEntryForm.label_scope3_category')}</FormLabel>
                <FormControl>
                  <Select
                    options={SCOPE3_CATEGORY_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('placeholder.selectCategory115')}
                    disabled={isApproved}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}

      <div className="space-y-4 rounded-lg border border-border/70 bg-muted/10 p-4">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">{t('activityDataEntryForm.heading_data_accuracy')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('activityDataEntryForm.desc_data_accuracy')}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="dataAccuracyScore"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('activityDataEntryForm.label_data_accuracy')}</FormLabel>
                <FormControl>
                  <Select
                    options={DATA_ACCURACY_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('placeholder.selectAScoreFrom1To10')}
                    disabled={isApproved}
                  />
                </FormControl>
                <FormDescription>
                  {t('activityDataEntryForm.desc_data_accuracy_score')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dataIsEstimated"
            render={({ field }) => (
              <FormItem className="rounded-md border border-border/60 bg-background/70 p-4">
                <div className="flex items-start gap-3">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked === true);
                      }}
                      disabled={isApproved}
                    />
                  </FormControl>
                  <div className="space-y-1">
                    <FormLabel>{t('activityDataEntryForm.label_mark_estimated')}</FormLabel>
                    <FormDescription>
                      {t('activityDataEntryForm.desc_mark_estimated')}
                    </FormDescription>
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="dataAssumptions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('activityDataEntryForm.label_assumptions')}</FormLabel>
              <FormControl>
                <Textarea
                  className="resize-none"
                  rows={4}
                  maxLength={2000}
                  disabled={isApproved}
                  placeholder={t(
                    'activityDataEntryForm.placeholder_describe_the_assumptions_used_for_data_g'
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('activityDataEntryForm.label_notes')}</FormLabel>
            <FormControl>
              <Textarea className="resize-none" rows={3} disabled={isApproved} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <Dialog
        open={autopilotDialogOpen}
        onOpenChange={(open) => {
          setAutopilotDialogOpen(open);
          if (!open) {
            setAutopilotSuggestion(null);
            setAutopilotSuggestions([]);
            setAutopilotSuggestionDataVersion('');
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('activityDataEntryForm.dialogTitle_confirm_autopilot_activity')}
            </DialogTitle>
            <DialogDescription>
              {t('activityDataEntryForm.dialogDesc_review_the_top_climatiq_autopilot_sugges')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 rounded-lg border bg-muted/10 p-4">
            {autopilotSuggestions.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  {autopilotSuggestions.length} suggestion{autopilotSuggestions.length > 1 ? 's' : ''} found — select one to confirm:
                </p>
                {autopilotSuggestions.map((suggestion) => {
                  const isSelected = autopilotSuggestion?.suggestionId === suggestion.suggestionId;
                  return (
                    <div
                      key={suggestion.suggestionId}
                      className={`cursor-pointer rounded-md border bg-background p-3 transition-colors hover:bg-accent/50 ${
                        isSelected ? 'ring-2 ring-primary border-primary' : ''
                      }`}
                      onClick={() => setAutopilotSuggestion(suggestion)}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{suggestion.emissionFactor.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {suggestion.emissionFactor.source} ·{' '}
                            {suggestion.emissionFactor.regionName ??
                              suggestion.emissionFactor.region}{' '}
                            · {String(suggestion.emissionFactor.year)} ·{' '}
                            {suggestion.emissionFactor.unit}
                          </p>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            suggestion.label === 'accept'
                              ? 'bg-emerald-100 text-emerald-900'
                              : 'bg-amber-100 text-amber-900'
                          }`}
                        >
                          {suggestion.label === 'accept' ? 'Accept' : 'Review'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No Autopilot suggestion generated.</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAutopilotDialogOpen(false);
                setAutopilotSuggestion(null);
                setAutopilotSuggestions([]);
              }}
            >
              {t('activityDataEntryForm.btn_cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleConfirmAutopilotSuggestion}
              disabled={!autopilotSuggestion}
            >
              Confirm Suggestion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={assignmentDialogOpen}
        onOpenChange={(open) => {
          if (confirmAssignmentMutation.isPending) return;
          setAssignmentDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('activityDataEntryForm.dialogTitle_confirm_activity_assignment')}
            </DialogTitle>
            <DialogDescription>
              {t('activityDataEntryForm.dialogDesc_review_ai_selected_activity_and_confirm_')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 rounded-lg border bg-muted/10 p-4">
            {assignmentResolveFactorQuery.isLoading || assignmentResolveFactorQuery.isFetching ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Resolving recommended factor...
              </div>
            ) : assignmentFactorCandidate ? (
              renderEmissionFactorCard({
                name: assignmentFactorCandidate.name,
                source: assignmentFactorCandidate.source,
                regionName: assignmentFactorCandidate.regionName,
                year: assignmentFactorCandidate.year,
                unit: assignmentFactorCandidate.unit,
                reason: assignmentFactorReason,
                factorId: assignmentFactorCandidate.id,
                activityId: assignmentFactorCandidate.activityId,
                unitType: assignmentFactorCandidate.unitType,
                dataVersion: assignmentFactorCandidate.dataVersion,
              })
            ) : assignmentAutopilotFactor ? (
              renderEmissionFactorCard({
                name: assignmentAutopilotFactor.name,
                source: assignmentAutopilotFactor.source,
                regionName: assignmentAutopilotFactor.regionName ?? assignmentAutopilotFactor.region,
                year: assignmentAutopilotFactor.year,
                unit: assignmentAutopilotFactor.unit,
                reason: `Climatiq Autopilot · ${String(Math.round((assignmentCandidate?.confidence ?? 0) * 100))}% confidence`,
                factorId: assignmentAutopilotFactor.id ?? '',
                activityId: assignmentAutopilotFactor.activityId ?? '',
                unitType: assignmentAutopilotFactor.unitType,
                dataVersion: undefined,
              })
            ) : assignmentCandidate ? (
              <ActivityAssignmentCandidateCard
                candidate={assignmentCandidate}
                trailing={
                  assignmentFactorMode ? (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${factorSelectionModeClasses(assignmentFactorMode)}`}
                    >
                      {factorSelectionModeLabel(assignmentFactorMode, t)}
                    </span>
                  ) : null
                }
              />
            ) : (
              <p className="text-sm text-muted-foreground">No assignment candidate generated.</p>
            )}
          </div>
          {/* Alternative candidates */}
          {showAlternatives && assignmentAlternatives.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                Other options ({assignmentAlternatives.length})
              </p>
              <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                {assignmentAlternatives.map((alt) => (
                  <button
                    key={alt.assignment.catalogEntryId}
                    type="button"
                    className="w-full text-left"
                    onClick={() => {
                      const currentTop: AlternativeCandidate = {
                        assignment: assignmentCandidate!,
                        autopilotFactor: assignmentAutopilotFactor,
                        autopilotSuggestionId: assignmentAutopilotSuggestionId,
                      };
                      setAssignmentAlternatives((prev) => [
                        currentTop,
                        ...prev.filter(
                          (a) =>
                            a.assignment.catalogEntryId !== alt.assignment.catalogEntryId
                        ),
                      ]);
                      setAssignmentCandidate(alt.assignment);
                      setAssignmentAutopilotFactor(alt.autopilotFactor ?? null);
                      setAssignmentAutopilotSuggestionId(alt.autopilotSuggestionId ?? null);
                    }}
                  >
                    {alt.autopilotFactor ? (
                      <div className="rounded-md border bg-muted/10 p-3 cursor-pointer transition-colors hover:border-primary/50 hover:bg-accent/50">
                        <p className="text-sm font-medium">{alt.autopilotFactor.name}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {alt.autopilotFactor.source} · {alt.autopilotFactor.regionName ?? alt.autopilotFactor.region} · {alt.autopilotFactor.year} · {alt.autopilotFactor.unit}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {alt.assignment.reasoning}
                        </p>
                      </div>
                    ) : (
                      <ActivityAssignmentCandidateCard
                        candidate={alt.assignment}
                        className="cursor-pointer transition-colors hover:border-primary/50 hover:bg-accent/50"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          {showAlternatives && assignmentAlternatives.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No additional candidates found for this description.
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAssignmentDialogOpen(false);
                setShowAlternatives(false);
              }}
              disabled={confirmAssignmentMutation.isPending}
            >
              {t('activityDataEntryForm.btn_cancel')}
            </Button>
            {!showAlternatives && assignmentAlternatives.length > 0 && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowAlternatives(true)}
                disabled={confirmAssignmentMutation.isPending}
              >
                Show alternatives ({assignmentAlternatives.length})
              </Button>
            )}
            <Button
              type="button"
              onClick={() => void handleConfirmAssignmentCandidate()}
              disabled={!assignmentCandidate || confirmAssignmentMutation.isPending}
            >
              {confirmAssignmentMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
          {t('activityDataEntryForm.btn_cancel')}
        </Button>
        {!isApproved && (
          <Button type="submit" disabled={isLoading || (mode === 'create' && !hasPeriods)}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Submit' : 'Update'}
          </Button>
        )}
      </div>
    </Form>
  );
}
